import React, { useEffect, useRef, useState } from "react";
import { PulsePreferences, PulseRange, RangePicker } from "./PulseReviewControls";
import { IrisIcon, IrisSparkIcon, ArrowUpRightIcon } from "./PulseIcons";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import MaleRoundedIcon from "@mui/icons-material/MaleRounded";
import FemaleRoundedIcon from "@mui/icons-material/FemaleRounded";
import { parseAgeGenderKey, countryFlagEmoji, countryName, AGE_BUCKET_ORDER } from "./pulseDemographics";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import BookmarkBorderRoundedIcon from "@mui/icons-material/BookmarkBorderRounded";
import BookmarkRoundedIcon from "@mui/icons-material/BookmarkRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import TrendingFlatRoundedIcon from "@mui/icons-material/TrendingFlatRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import TrendingDownRoundedIcon from "@mui/icons-material/TrendingDownRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import MailOutlineRoundedIcon from "@mui/icons-material/MailOutlineRounded";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import PushPinOutlinedIcon from "@mui/icons-material/PushPinOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import ShareRoundedIcon from "@mui/icons-material/ShareRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import WavingHandRoundedIcon from "@mui/icons-material/WavingHandRounded";
import InstagramIcon from "@mui/icons-material/Instagram";
import YouTubeIcon from "@mui/icons-material/YouTube";
import PhotoCameraRoundedIcon from "@mui/icons-material/PhotoCameraRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import LogoutRoundedIcon from "@mui/icons-material/LogoutRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import NotificationsNoneRoundedIcon from "@mui/icons-material/NotificationsNoneRounded";
import TextFieldsRoundedIcon from "@mui/icons-material/TextFieldsRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import GridViewRoundedIcon from "@mui/icons-material/GridViewRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import TipsAndUpdatesOutlinedIcon from "@mui/icons-material/TipsAndUpdatesOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import BoltOutlinedIcon from "@mui/icons-material/BoltOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import ShowChartRoundedIcon from "@mui/icons-material/ShowChartRounded";
import DragIndicatorRoundedIcon from "@mui/icons-material/DragIndicatorRounded";
import VisibilityOffRoundedIcon from "@mui/icons-material/VisibilityOffRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { API_BASE, pulseApi } from "../../lib/authApi";

// Shared floor/ceiling for the Settings -> Appearance text-size stepper,
// consumed by both the clamp in PulsePage's handleTextSizeStep and the
// disabled state of the +/- buttons below (and the Reading & IRIS dropdown's
// own "Standard" = 1.0 minimum option).
export const TEXT_SCALE_MIN = 1;
// Backend's real ceiling (pulse-backend's UpdateAccountPreferencesDto /
// UpdatePreferencesDto both @Max(1.4)) - NOT 1.5. Stepping past 1.4 gets a
// 400 from PUT /api/account-preferences, so the UI bound must match it.
export const TEXT_SCALE_MAX = 1.4;

export type DesignerTrendPoint = {
  label: string;
  shortForm: number;
  longForm: number;
  carousel?: number;
};

export type DesignerWidgetStatus = {
  ready: boolean;
  synthetic: boolean;
  reason?: string;
  sample_days?: number;
  min_sample_days?: number;
};

export type DesignerMetrics = {
  header: { title: string; subtitle: string; growth_badge: string };
  pulse_score: {
    score: number;
    delta_text: string;
    performance: string;
    formula?: string;
    score_ready?: boolean;
    raw_score?: number;
    weighted_subscores?: Record<string, number>;
    sample_days?: number;
    min_sample_days?: number;
    video_sample_count?: number;
    video_count?: number;
    min_videos?: number;
    videos_remaining?: number;
    confidence?: string;
    score_state?: string;
    state_message?: string;
    weekly_trend?: Array<{ week_start: string; week_end: string; score: number | null }>;
  };
  shorts_score?: {
    score: number;
    score_ready?: boolean;
    score_state?: "ready" | "provisional" | "insufficient_data" | string;
    weighted_subscores?: Record<string, number>;
    confidence?: string;
    sample_days?: number;
    shorts_read?: number;
    shorts_video_count?: number;
    video_count?: number;
    min_videos?: number;
    videos_remaining?: number;
    formula?: string;
    state_message?: string;
    delta_text?: string;
    score_delta?: number;
    comparison_available?: boolean;
    weekly_trend?: Array<{ week_start: string; week_end: string; score: number | null }>;
  } | null;
  engagement_rate: { value: number; delta: number; comparison_available?: boolean };
  period_metrics?: {
    views: number;
    watch_hours: number;
    subscribers_net: number;
    sample_days: number;
    start_date: string;
    end_date: string;
    views_delta_pct?: number;
    watch_hours_delta_pct?: number;
    subscribers_delta_pct?: number;
  };
  best_format: { label: string; share: number };
  optimal_time: { label: string; delta: number };
  performance_trends: DesignerTrendPoint[];
  content_mix: { short: number; long: number; carousel: number; total_videos?: number };
  topic_window?: string;
  topic_performance: Array<{ label: string; count: number }>;
  audience_activity: number[];
  audience_activity_detail?: Array<{
    label: string;
    score: number;
    avg_views?: number;
  }>;
  audience_activity_heatmap?: {
    days: string[];
    hours: string[];
    matrix: number[][];
  } | null;
};

// Plan.items can carry past cycles' resolved/expired moves alongside the current ones -
// the Insights "View all" list only ever renders the first PLAN_VISIBLE_MOVES of them, so
// any "X/Y" count elsewhere must total that same visible window, not the full history.
const PLAN_VISIBLE_MOVES = 4;

export type DesignerPlanItem = {
  id?: string;
  title: string;
  detail: string;
  status: "proposed" | "pending" | "verified" | "no_move" | "expired" | "done";
  score_lift: number;
  pillar?: string;
  pillar_label?: string;
  metric_key?: string;
  metric_label?: string;
  target_delta_pct?: number;
  evaluation_days?: number;
  measured_days?: number;
  result_delta_pct?: number | null;
  evidence?: string;
  measurement_phase?: string;
  origin?: "algorithmic" | "manual";
  saved_item_id?: string | null;
  evaluated_at?: string | null;
};

export type DesignerPlan = {
  title: string;
  subtitle: string;
  potential_score_lift: number;
  items: DesignerPlanItem[];
};

export type DesignerRecommendation = {
  title: string;
  summary: string;
  action: string;
  confidence: string;
  based_on: string[];
  state: string;
};

export type DesignerPreview = {
  channel_id?: string | null;
  platform?: string | null;
  title?: string | null;
  custom_url?: string | null;
  description?: string | null;
  thumbnail_url?: string | null;
  cover_url?: string | null;
  subscriber_count?: number | null;
  view_count?: number | null;
  video_count?: number | null;
};

export type DesignerInstagramConnection = {
  connected: boolean;
  status: string;
  account_id?: string | null;
  username?: string | null;
  name?: string | null;
  account_type?: string | null;
  profile_picture_url?: string | null;
  followers_count?: number | null;
  media_count?: number | null;
  last_data_pull_at?: string | null;
};

export type DesignerMediaPoint = { label: string; value: string };
export type DesignerTopContent = { title: string; views: number; engagement_rate: number; url?: string };
export type DesignerMediaMetrics = {
  title: string;
  subtitle: string;
  followers: number;
  avg_monthly_reach: number;
  engagement_rate: number;
  avg_comments: number;
  audience_age_distribution: DesignerMediaPoint[];
  audience_geo_distribution: DesignerMediaPoint[];
  top_content_subtitle: string;
  top_content: DesignerTopContent[];
  top_content_is_inspiration?: boolean;
};

type MediaKitAccess = {
  share_url: string;
  require_passcode: boolean;
  passcode: string | null;
  regenerate_passcode?: boolean;
  viewer_watermark: boolean;
  expiration_enabled: boolean;
  expires_at: string | null;
  notify_on_view: boolean;
  approved_emails: string[];
  recent_activity: Array<{ id: string; viewer_name: string; viewed_at: string }>;
};

export type DesignerSavedItem = {
  id: string;
  type: "metric" | "action";
  title: string;
  body: string;
  value?: string | null;
  pinned: boolean;
  category?: string | null;
  created_at?: string | null;
};

export type DesignerTypedInsight = {
  id: string;
  type: "notable_trend" | "missed_opportunity" | "watch_out" | "new_value";
  type_label: string;
  title: string;
  body: string;
  confidence: string;
  surface: string;
  evidence: string;
  action_prompt: string;
};

export type DesignerMemory = {
  channel_summary?: string;
  latest_conversation_summary?: string;
  recommendations_given?: string[];
  open_unknowns?: string[];
  user_preferences?: string[];
  topics?: Array<{ topic: string; summary: string; updated_at?: string }>;
  updated_at?: string;
};

type Account = { creatorName: string; youtubeChannel: string };

export type DesignerProfile = {
  display_name: string | null;
  handle: string | null;
  bio: string | null;
  avatar_url: string | null;
};

const compactNumber = (value?: number | null) =>
  Intl.NumberFormat("en", { notation: "compact", maximumFractionDigits: 1 }).format(Math.max(0, Number(value || 0)));

const scoreValue = (value: unknown) => Math.max(0, Math.min(10, Number(value || 0)));

const firstName = (account: Account, preview: DesignerPreview | null) =>
  String(account.creatorName || "Creator").trim().split(/\s+/)[0] || "Creator";

const creatorName = (account: Account, preview: DesignerPreview | null) =>
  String(account.creatorName || preview?.title || "Creator").trim() || "Creator";

const creatorHandle = (account: Account, preview: DesignerPreview | null) => {
  const source = String(preview?.custom_url || account.youtubeChannel || "").trim();
  if (!source) return "@creator";
  const clean = source.replace(/^https?:\/\/(www\.)?youtube\.com\//i, "").replace(/^channel\//i, "");
  return clean.startsWith("@") ? clean : `@${clean}`;
};

const initials = (name: string) =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase() || "P";

const parsePercent = (value: string) => Math.max(0, Math.min(100, Number.parseFloat(value) || 0));

const formatAccessTime = (value: string) => {
  const elapsedHours = Math.max(0, Math.round((Date.now() - new Date(value).getTime()) / 3600000));
  if (elapsedHours < 1) return "just now";
  if (elapsedHours < 24) return `${elapsedHours} hour${elapsedHours === 1 ? "" : "s"} ago`;
  const days = Math.round(elapsedHours / 24);
  return days === 1 ? "yesterday" : `${days} days ago`;
};

const moveStatusLabel = (item: DesignerPlanItem) => {
  const evaluatedSuffix = item.evaluated_at ? ` - evaluated ${formatAccessTime(item.evaluated_at)}` : "";
  // A manual move (bookmarked from a Saved Action) has no metric/baseline to
  // measure - never claim IRIS is tracking or has verified it.
  if (item.origin === "manual") return item.status === "done" ? "Marked done (self-reported)" : "Self-reported - not yet marked done";
  if (item.status === "verified") return `Verified by analytics${evaluatedSuffix}`;
  if (item.measurement_phase === "collecting_baseline") return "Committed - collecting baseline";
  if (item.status === "pending") return `Measurement pending${item.measured_days ? ` - ${item.measured_days}/${item.evaluation_days || 7} days` : ""}`;
  if (item.status === "no_move") {
    const delta = item.result_delta_pct != null ? `${item.result_delta_pct >= 0 ? "+" : ""}${item.result_delta_pct.toFixed(1)}%` : null;
    const target = item.target_delta_pct != null ? `${item.target_delta_pct}%` : null;
    return `${delta && target ? `Didn't move - ${delta} vs ${target} target` : "Measured, but didn't hit target"}${evaluatedSuffix}`;
  }
  if (item.status === "expired") return `Couldn't be measured - no usable data in the window. Re-commit to try again.${evaluatedSuffix}`;
  return "Commit move";
};

// Semicircle gauge for the Pulse Score page's surface cards - deliberately a separate component
// from PulseRing (the full-circle donut Home still uses), not a variant of it, since the two
// shapes need genuinely different SVG/CSS approaches. Gradient (cyan -> purple -> pink) is a
// single fixed paint across the full arc width, revealed proportionally via stroke-dasharray -
// matches the reference's behavior where a lower score simply shows less of the same gradient
// (stopping around purple) rather than a differently-colored gradient.
const PulseSemicircleGauge: React.FC<{ score: number; pending?: boolean; gradientId: string }> = ({ score, pending, gradientId }) => {
  const value = scoreValue(score);
  const fraction = pending ? 0 : Math.max(0, Math.min(1, value / 10));
  const width = 168;
  const height = 94;
  const r = 66;
  const strokeWidth = 14;
  const cx = width / 2;
  const cy = height - 8;
  const totalLength = Math.PI * r;
  const filledLength = totalLength * fraction;
  const arcPath = `M ${cx - r},${cy} A ${r},${r} 0 0 1 ${cx + r},${cy}`;
  return (
    <div className="pulse-v2-semigauge">
      <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={`Score ${pending ? "pending" : value.toFixed(1)} of 10`}>
        <defs>
          <linearGradient id={gradientId} x1={cx - r} y1={cy} x2={cx + r} y2={cy} gradientUnits="userSpaceOnUse">
            <stop offset="0%" stopColor="#4fc3e0" />
            <stop offset="45%" stopColor="#7c5cf0" />
            <stop offset="100%" stopColor="#ef2f9e" />
          </linearGradient>
        </defs>
        <path d={arcPath} fill="none" stroke="#e3e1ea" strokeWidth={strokeWidth} strokeLinecap="round" />
        {!pending && (
          <path
            d={arcPath}
            fill="none"
            stroke={`url(#${gradientId})`}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={`${filledLength} ${totalLength}`}
          />
        )}
      </svg>
      <div className="pulse-v2-semigauge-value">
        <strong>{pending ? "--" : value.toFixed(1)}</strong>
        <span>of 10</span>
      </div>
    </div>
  );
};

// The shared "not enough data" glyph - a greyed speedometer in a soft circle, used both
// at small size inline on a per-surface score card and at large size on the full-width
// no-data takeover card, so the two honest-empty states read as the same visual language.
const NoDataGaugeIcon: React.FC<{ size?: number }> = ({ size = 40 }) => (
  <span className="pulse-v2-no-data-gauge" style={{ width: size, height: size }}>
    <svg viewBox="0 0 40 40" width={size} height={size} role="img" aria-hidden="true">
      <path d="M 10,25 A 12,12 0 0 1 30,25" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
      <line x1="20" y1="25" x2="25.5" y2="17.5" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
    </svg>
  </span>
);

// Honest copy for a surface that hasn't hit its own upload-count gate yet. Always prefers
// the backend's own state_message (it already reflects min_videos/video_count precisely);
// the fallback only fires for an older payload shape that hasn't sent state_message yet,
// and still pulls the real gate number rather than hardcoding "10" separately.
const notEnoughDataCopy = (surfaceLabel: string, surface: { state_message?: string; min_videos?: number } | null | undefined) =>
  surface?.state_message || `There just isn't enough ${surfaceLabel} history to place a reliable score. Unlocks at roughly ${surface?.min_videos ?? 10} videos.`;

// A score is real and worth showing whenever score_state is "ready" or "provisional" -
// only "insufficient_data" (or, for backends not yet sending score_state, a false
// score_ready) means there is genuinely nothing to show. Gating on score_ready alone
// hid real "provisional" scores (an early but real interim value) behind the same
// pending/"--" state as no-data-at-all, which is wrong - provisional still gets a real
// number, just with a distinct "Provisional" label instead of a confidence tier.
const hasRealScore = (score: { score_ready?: boolean; score_state?: string } | null | undefined) => {
  if (!score) return false;
  if (score.score_state === "insufficient_data") return false;
  if (score.score_state === "ready" || score.score_state === "provisional") return true;
  return Boolean(score.score_ready);
};

const scoreConfidence = (metrics: DesignerMetrics) => {
  if (!hasRealScore(metrics.pulse_score)) return "Pending";
  if (metrics.pulse_score.score_state === "provisional") return "Provisional";
  return metrics.pulse_score.confidence || "low";
};

const Avatar: React.FC<{ account: Account; preview: DesignerPreview | null; large?: boolean }> = ({ account, preview, large }) => {
  const name = creatorName(account, preview);
  return (
    <span className={`pulse-v2-avatar ${large ? "large" : ""}`}>
      {preview?.thumbnail_url ? <img src={preview.thumbnail_url} alt="" referrerPolicy="no-referrer" /> : initials(name)}
    </span>
  );
};

const EmptyMetric: React.FC<{ label?: string }> = ({ label = "Data pending" }) => (
  <span className="pulse-v2-pending">{label}</span>
);

const InsightsPending: React.FC<{ status?: DesignerWidgetStatus; fallback: string }> = ({ status, fallback }) => {
  const samples = status?.sample_days;
  const target = status?.min_sample_days;
  const progress = samples !== undefined && target !== undefined ? ` (${samples}/${target} days collected)` : "";
  return <div className="pulse-v2-chart-pending"><AutoAwesomeRoundedIcon /><strong>Building this insight</strong><span>{fallback}{progress}.</span></div>;
};

const TREND_SERIES: Array<{ key: "shortForm" | "longForm" | "carousel"; className: string; label: string }> = [
  { key: "shortForm", className: "short", label: "Short-form" },
  { key: "longForm", className: "long", label: "Long-form" },
  { key: "carousel", className: "carousel", label: "Carousel" },
];

const InsightsTrendChart: React.FC<{ points: DesignerTrendPoint[] }> = ({ points }) => {
  const width = 680;
  const height = 190;
  const left = 24;
  const right = 12;
  const top = 12;
  const bottom = 30;
  const hasCarousel = points.some((point) => point.carousel !== undefined);
  const series = hasCarousel ? TREND_SERIES : TREND_SERIES.filter((entry) => entry.key !== "carousel");
  const values = points.flatMap((point) => series.map((entry) => Number(point[entry.key] || 0))).filter(Number.isFinite);
  const maxValue = Math.max(1, Math.ceil(Math.max(...values, 1)));
  const x = (index: number) => left + (index * (width - left - right)) / Math.max(1, points.length - 1);
  const y = (value: number) => top + (1 - Math.max(0, value) / maxValue) * (height - top - bottom);
  const line = (key: "shortForm" | "longForm" | "carousel") => points.map((point, index) => `${x(index)},${y(Number(point[key] || 0))}`).join(" ");
  const ticks = [0, 0.25, 0.5, 0.75, 1];

  return (
    <div className="pulse-v2-trend-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Engagement rate by content format trend">
        {ticks.map((tick) => {
          const tickY = top + (1 - tick) * (height - top - bottom);
          return (
            <g key={tick}>
              <line x1={left} x2={width - right} y1={tickY} y2={tickY} />
              <text x={left - 8} y={tickY + 4} textAnchor="end">{Math.round(maxValue * tick)}</text>
            </g>
          );
        })}
        {series.map((entry) => (
          <polyline key={entry.key} className={entry.className} points={line(entry.key)} />
        ))}
        {series.map((entry) => points.map((point, index) => (
          <circle key={`${entry.key}-${point.label}-${index}`} className={entry.className} cx={x(index)} cy={y(Number(point[entry.key] || 0))} r="3" />
        )))}
        {points.map((point, index) => (
          <text key={`label-${point.label}-${index}`} className="label" x={x(index)} y={height - 8} textAnchor="middle">{point.label}</text>
        ))}
      </svg>
      <div className="pulse-v2-chart-legend">
        {series.map((entry) => <span key={entry.key}><i className={entry.className} />{entry.label}</span>)}
      </div>
    </div>
  );
};

// Single-series area chart for the Pulse Score trend modal - deliberately separate from
// InsightsTrendChart above, which is hardcoded to the short/long/carousel content-mix series
// and always renders a legend. The score trend is one real number per week, axis scaled to the
// data range (not a fixed 0-N scale), no legend, no second series.
// Standard "nice numbers" axis algorithm (1/2/2.5/5/10 x 10^n steps) - replaces the old fixed
// 0.25-granularity rounding, which produced cramped, ugly ticks like 1.25/1.63/2/2.38/2.75 for
// some data ranges. Always returns a small number of evenly-spaced, human-readable tick values.
const niceAxis = (rawMin: number, rawMax: number, targetTicks = 4) => {
  const span = Math.max(0.5, rawMax - rawMin);
  const roughStep = span / targetTicks;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;
  const niceNormalized = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
  const step = niceNormalized * magnitude;
  const min = Math.floor(rawMin / step) * step;
  const max = Math.ceil(rawMax / step) * step;
  const ticks: number[] = [];
  for (let value = min; value <= max + step / 2; value += step) ticks.push(Math.round(value * 100) / 100);
  return { min, max, ticks };
};

// Catmull-Rom-to-Bezier conversion for a smooth, natural curve through a run of points - used
// instead of straight polyline segments so the trend reads as a smooth area chart like the
// reference, not a jagged connect-the-dots line.
const smoothPath = (pts: Array<{ x: number; y: number }>) => {
  if (pts.length < 2) return pts.length === 1 ? `M ${pts[0].x},${pts[0].y}` : "";
  let d = `M ${pts[0].x},${pts[0].y}`;
  for (let i = 0; i < pts.length - 1; i++) {
    const p0 = pts[i - 1] || pts[i];
    const p1 = pts[i];
    const p2 = pts[i + 1];
    const p3 = pts[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x},${cp1y} ${cp2x},${cp2y} ${p2.x},${p2.y}`;
  }
  return d;
};

const ScoreTrendChart: React.FC<{ points: Array<{ label: string; score: number | null }> }> = ({ points }) => {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const width = 680;
  const height = 190;
  const left = 34;
  const right = 12;
  const top = 12;
  const bottom = 30;
  const values = points.map((point) => point.score).filter((score): score is number => score !== null);
  const rawMin = Math.min(...values, values[0] ?? 0);
  const rawMax = Math.max(...values, values[0] ?? 1);
  const pad = Math.max(0.4, (rawMax - rawMin) * 0.2);
  const { min, max, ticks } = niceAxis(rawMin - pad, rawMax + pad);
  const span = Math.max(0.25, max - min);
  const x = (index: number) => left + (index * (width - left - right)) / Math.max(1, points.length - 1);
  const y = (value: number) => top + (1 - (value - min) / span) * (height - top - bottom);

  // A null score is a genuine gap (insufficient_data that week), never interpolated or treated
  // as 0 - build separate line/area segments per contiguous run of real scores so the chart
  // visually breaks instead of drawing a fake connecting line across the gap.
  const segments: Array<Array<{ index: number; score: number }>> = [];
  points.forEach((point, index) => {
    if (point.score === null) return;
    const last = segments[segments.length - 1];
    if (last && last[last.length - 1].index === index - 1) last.push({ index, score: point.score });
    else segments.push([{ index, score: point.score }]);
  });

  const hoverScore = hoverIndex !== null ? points[hoverIndex].score : null;
  const hitWidth = (width - left - right) / Math.max(1, points.length - 1);

  return (
    <div className="pulse-v2-score-trend-chart">
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Pulse Score 7-week trend" onMouseLeave={() => setHoverIndex(null)}>
        <defs>
          <linearGradient id="pulseScoreTrendFill" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#8428f5" stopOpacity="0.28" />
            <stop offset="100%" stopColor="#8428f5" stopOpacity="0" />
          </linearGradient>
        </defs>
        {ticks.map((tickValue) => {
          const tickY = y(tickValue);
          return (
            <g key={tickValue}>
              <line x1={left} x2={width - right} y1={tickY} y2={tickY} />
              <text x={left - 8} y={tickY + 4} textAnchor="end">{tickValue}</text>
            </g>
          );
        })}
        {segments.map((segment, segmentIndex) => {
          const pts = segment.map((point) => ({ x: x(point.index), y: y(point.score) }));
          const linePath = smoothPath(pts);
          const baseline = y(min);
          const areaPath = `${linePath} L ${pts[pts.length - 1].x},${baseline} L ${pts[0].x},${baseline} Z`;
          return (
            <g key={segmentIndex}>
              <path className="area" d={areaPath} fill="url(#pulseScoreTrendFill)" stroke="none" />
              <path className="line" d={linePath} fill="none" />
            </g>
          );
        })}
        {hoverIndex !== null && (
          <line className="hover-marker" x1={x(hoverIndex)} x2={x(hoverIndex)} y1={top} y2={height - bottom} />
        )}
        {hoverIndex !== null && hoverScore !== null && (
          <circle className="hover-dot" cx={x(hoverIndex)} cy={y(hoverScore)} r="4" />
        )}
        {points.map((point, index) => (
          <text key={`label-${point.label}-${index}`} className="label" x={x(index)} y={height - 8} textAnchor="middle">{point.label}</text>
        ))}
        {points.map((point, index) => (
          <rect
            key={`hit-${point.label}-${index}`}
            className="hit-area"
            x={x(index) - hitWidth / 2}
            y={top}
            width={hitWidth}
            height={height - top - bottom}
            fill="transparent"
            onMouseEnter={() => setHoverIndex(index)}
          />
        ))}
      </svg>
      {hoverIndex !== null && (
        <div className="pulse-v2-score-trend-tooltip" style={{ left: `${(x(hoverIndex) / width) * 100}%` }}>
          <strong>{points[hoverIndex].label}</strong>
          <span>{hoverScore !== null ? `${hoverScore.toFixed(1)} score` : "No data this week"}</span>
        </div>
      )}
    </div>
  );
};

const ScoreBreakdown: React.FC<{
  weightedSubscores?: Record<string, number>;
  ready: boolean;
  confidence: string;
  showConfidencePills?: boolean;
}> = ({ weightedSubscores, ready, confidence, showConfidencePills = true }) => {
  const values = weightedSubscores || {};
  const rows = [
    ["Content Strategy", "content_strategy", TipsAndUpdatesOutlinedIcon],
    ["Audience Growth & Retention", "audience_growth_retention", GroupsOutlinedIcon],
    ["Execution Craft", "execution_craft", BoltOutlinedIcon],
    ["Publishing Discipline", "publishing_discipline", CalendarMonthOutlinedIcon],
  ] as const;
  return (
    <div className="pulse-v2-breakdown">
      {rows.map(([label, key, Icon]) => {
        const value = scoreValue(values[key]);
        const available = ready && Number.isFinite(Number(values[key]));
        const rowConfidence = available ? confidence : "Pending";
        return (
          <div className={`pulse-v2-breakdown-row ${showConfidencePills ? "" : "no-pill"}`} key={key}>
            <span className="pulse-v2-breakdown-icon"><Icon fontSize="inherit" /></span>
            <strong>{label}</strong>
            <span className="pulse-v2-progress"><i style={{ width: available ? `${value * 10}%` : "0%" }} /></span>
            <b>{available ? value.toFixed(1) : "--"}</b>
            {showConfidencePills ? <em className={rowConfidence.toLowerCase()}>{rowConfidence}</em> : null}
          </div>
        );
      })}
    </div>
  );
};

export const PulseHomeDashboard: React.FC<{
  metrics: DesignerMetrics;
  scoreStatus?: DesignerWidgetStatus;
  channelDataState?: "ready" | "low_data" | "no_data";
  plan: DesignerPlan | null;
  planError?: string | null;
  recommendation: DesignerRecommendation | null;
  account: Account;
  preview: DesignerPreview | null;
  chatDraft: string;
  chatLoading: boolean;
  onDraftChange: (value: string) => void;
  onAskIris: () => void;
  onSuggestion: (value: string) => void;
  onOpenInsights: () => void;
  onOpenScore: () => void;
  onCommitMove: (item: DesignerPlanItem) => void;
  onAskAboutSurface: (label: string, shortLabel: string, score: number | null) => void;
}> = ({ metrics, scoreStatus, channelDataState, plan, planError, recommendation, account, preview, chatDraft, chatLoading, onDraftChange, onAskIris, onSuggestion, onOpenInsights, onOpenScore, onCommitMove, onAskAboutSurface }) => {
  const name = firstName(account, preview);
  // Home shows one quick-glance score, so it must pick the surface the creator is actually
  // active on - a Shorts-first channel was always shown its (empty/low) Video score here even
  // though the Pulse Score detail page scores both surfaces independently. Compare published
  // counts and surface whichever has more; an exact tie (including both 0, e.g. a brand-new
  // channel) keeps Video, matching this card's pre-existing behavior - there's no signal to
  // prefer Shorts over Video when they're even.
  const videosPublished = metrics.pulse_score.video_count ?? 0;
  const shortsPublished = metrics.shorts_score?.video_count ?? metrics.shorts_score?.shorts_video_count ?? 0;
  const activeSurface: "videos" | "shorts" = shortsPublished > videosPublished ? "shorts" : "videos";
  const activeScore = activeSurface === "shorts" ? metrics.shorts_score : metrics.pulse_score;
  const score = scoreValue(activeScore?.score);
  const scoreReady = hasRealScore(activeScore) && scoreStatus?.synthetic !== true;
  // channelDataState is already a whole-channel ("both surfaces short") summary, so it still
  // applies regardless of which surface won the comparison above.
  const homeScoreInsufficient = channelDataState === "no_data" || !activeScore || activeScore.score_state === "insufficient_data";
  const activeConfidence = !scoreReady ? "Pending" : activeScore?.score_state === "provisional" ? "Provisional" : (activeScore?.confidence || "low");
  const activeDeltaText = activeScore?.delta_text || null;
  const today = new Intl.DateTimeFormat("en-GB", { weekday: "long", day: "numeric", month: "long" }).format(new Date());
  const actionItems = (plan?.items || []).slice(0, 2);
  // The "X/Y" badge must total the SAME set "View all in Insights" renders (the first
  // PLAN_VISIBLE_MOVES items), not plan.items.length - that full array also carries past
  // cycles' resolved/expired moves, which inflated the denominator (e.g. "0/11") well past
  // the 4 moves Insights actually shows.
  const visibleMoveItems = (plan?.items || []).slice(0, PLAN_VISIBLE_MOVES);
  const totalMoveCount = visibleMoveItems.length;
  const verifiedMoveCount = visibleMoveItems.filter((item) => item.status === "verified").length;
  const subscores = activeScore?.weighted_subscores || {};
  const breakdownRows = [
    ["Content Strategy", subscores.content_strategy],
    ["Execution Craft", subscores.execution_craft],
    ["Audience Growth & Retention", subscores.audience_growth_retention],
    ["Publishing Discipline", subscores.publishing_discipline],
  ] as const;
  return (
    <div className="pulse-v2-page pulse-v2-home">
      <header className="pulse-v2-home-head">
        <p>{today}</p>
        <h1>Good morning, {name} <WavingHandRoundedIcon /></h1>
        <span>Your quick-glance overview: Pulse Score, prescribed moves, and what IRIS has been watching.</span>
      </header>

      <div className="pulse-v2-ask-wrap">
        <div className="pulse-v2-ask-glow" />
        <div className="pulse-v2-askbar">
          <input
            value={chatDraft}
            onChange={(event) => onDraftChange(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === "Enter") {
                event.preventDefault();
                onAskIris();
              }
            }}
            placeholder="Ask IRIS about your content..."
          />
          <button type="button" onClick={onAskIris} disabled={chatLoading || !chatDraft.trim()}>
            <IrisIcon /> {chatLoading ? "Thinking" : "Ask IRIS"}
          </button>
        </div>
        <div className="pulse-v2-prompt-chips">
          {["Why did engagement dip this week?", "Draft a move for my weakest pillar", "Compare this month vs last"].map((prompt) => (
            <button type="button" key={prompt} onClick={() => onSuggestion(prompt)}>
              {prompt} <ArrowUpRightIcon />
            </button>
          ))}
        </div>
      </div>

      <section className="pulse-v2-card pulse-v2-home-score pulse-v2-iris-jump-host">
        <div className="pulse-v2-card-topline">
          <div><span className="pulse-v2-eyebrow">Pulse Score</span><h2>Your quick-glance score</h2></div>
          {/* Shorts doesn't carry a delta_text from the backend yet - omit the pill rather
              than show stale/fabricated Video growth text under a Shorts score. */}
          {activeDeltaText ? <span className="pulse-v2-growth">{activeDeltaText}</span> : null}
        </div>
        {homeScoreInsufficient ? (
          <div className="pulse-v2-home-score-body pulse-v2-no-data-card" style={{ padding: "24px 0" }}>
            <NoDataGaugeIcon size={48} />
            <h2>Not enough data to score yet</h2>
            <p>{notEnoughDataCopy(activeSurface === "shorts" ? "shorts" : "video", activeScore)}</p>
          </div>
        ) : (
          <div className="pulse-v2-home-score-body">
            <PulseSemicircleGauge score={score} pending={!scoreReady} gradientId="pulseScoreGaugeHome" />
            <div className="pulse-v2-home-score-copy">
              <strong>{activeSurface === "shorts" ? "YouTube - Shorts" : "YouTube - Videos"}</strong>
              <span className={`pulse-v2-confidence ${activeConfidence.toLowerCase()}`}>{activeScore?.score_state === "provisional" ? "Provisional - " : ""}{activeConfidence}</span>
              <small>
                {activeSurface === "shorts"
                  ? (metrics.shorts_score?.shorts_video_count ? `${compactNumber(metrics.shorts_score.shorts_video_count)} shorts read` : `${scoreStatus?.sample_days || 0} daily snapshots read`)
                  : (metrics.pulse_score.video_sample_count ? `${compactNumber(metrics.pulse_score.video_sample_count)} long-form videos read` : `${scoreStatus?.sample_days || 0} daily snapshots read`)}
              </small>
            </div>
            <div className="pulse-v2-home-breakdown">
              {breakdownRows.map(([label, value]) => <div key={label}><i /><span>{label}</span><strong>{scoreReady && value !== undefined ? scoreValue(value).toFixed(1) : "--"}</strong></div>)}
            </div>
          </div>
        )}
        <button type="button" className="pulse-v2-home-score-cta" onClick={onOpenScore}>View Full Pulse Score <span>-&gt;</span></button>
        <button
          type="button"
          className="pulse-v2-score-card-iris-jump"
          aria-label="Ask IRIS about your Pulse Score"
          onClick={() => onAskAboutSurface(activeSurface === "shorts" ? "YouTube - Shorts" : "YouTube - Videos", activeSurface === "shorts" ? "Shorts" : "Videos", score)}
        >
          <IrisSparkIcon size={16} />
        </button>
      </section>

      <div className="pulse-v2-home-grid">
        <section className="pulse-v2-card pulse-v2-home-column pulse-v2-iris-jump-host">
          <div className="pulse-v2-card-topline"><div><span className="pulse-v2-eyebrow">Prescribed moves</span><h2>This week's moves</h2></div><span>{verifiedMoveCount}/{Math.max(1, totalMoveCount)}</span></div>
          {planError ? <div className="pulse-v2-action-message error">{planError}</div> : null}
          {actionItems.length ? actionItems.map((item, index) => (
            <button className={`pulse-v2-home-move ${item.status}`} key={item.id || `${item.title}-${index}`} type="button" onClick={() => item.status === "proposed" && onCommitMove(item)} disabled={item.status !== "proposed"}>
              <span className={item.status === "verified" ? "checked" : ""}>{item.status === "verified" ? <CheckRoundedIcon fontSize="inherit" /> : item.status === "pending" ? <AutoAwesomeRoundedIcon fontSize="inherit" /> : item.status === "no_move" ? <TrendingFlatRoundedIcon fontSize="inherit" /> : item.status === "expired" ? <HelpOutlineRoundedIcon fontSize="inherit" /> : null}</span>
              <div><strong>{item.title}</strong><em>{item.pillar_label || "Measured move"}</em><small>{moveStatusLabel(item)}</small></div>
            </button>
          )) : <EmptyMetric label="Moves are being prepared" />}
          <button type="button" className="pulse-v2-text-action" onClick={onOpenInsights}>View all in Insights -&gt;</button>
          <button
            type="button"
            className="pulse-v2-score-card-iris-jump"
            aria-label="Ask IRIS about this week's moves"
            onClick={() => onAskAboutSurface("This week's moves", "your prescribed moves", null)}
          >
            <IrisSparkIcon size={16} />
          </button>
        </section>

        <section className="pulse-v2-card pulse-v2-home-column pulse-v2-iris-jump-host">
          <span className="pulse-v2-eyebrow">Move updates</span><h2>What's changed</h2>
          <div className="pulse-v2-status-line success"><span><CheckRoundedIcon fontSize="inherit" /></span><div><strong>{plan?.items?.find((item) => item.status === "verified")?.title || "Metrics connected"}</strong><small>{plan?.items?.some((item) => item.status === "verified") ? "Verified by measured performance" : "IRIS is watching this signal"}</small></div></div>
          <div className="pulse-v2-status-line active"><span /><div><strong>{plan?.items?.find((item) => item.status === "pending")?.title || "No move committed yet"}</strong><small>{plan?.items?.some((item) => item.status === "pending") ? "Measurement pending" : "Commit a prescribed move to start measurement"}</small></div></div>
          <div className="pulse-v2-status-line warning"><span>!</span><div><strong>{recommendation?.title || "Next recommendation"}</strong><small>{recommendation?.confidence || "Pending"} confidence</small></div></div>
          <button
            type="button"
            className="pulse-v2-score-card-iris-jump"
            aria-label="Ask IRIS about what's changed"
            onClick={() => onAskAboutSurface("Move updates", "what's changed", null)}
          >
            <IrisSparkIcon size={16} />
          </button>
        </section>

        <section className="pulse-v2-card pulse-v2-home-column pulse-v2-iris-jump-host">
          <span className="pulse-v2-eyebrow">This week</span><h2>At a glance</h2>
          <p className="pulse-v2-glance"><span>[+]</span>{preview?.video_count ? `${preview.video_count} videos available for analysis` : "Video history is being collected"}</p>
          <p className="pulse-v2-glance"><span>[!]</span>{metrics.optimal_time.label || "Posting-time experiment pending"}</p>
          <p className="pulse-v2-glance"><span>[/]</span>{scoreReady ? (activeDeltaText ? `Pulse Score is ${activeDeltaText}` : "Pulse Score is up to date") : "Pulse Score is building its baseline"}</p>
          <button
            type="button"
            className="pulse-v2-score-card-iris-jump"
            aria-label="Ask IRIS about this week at a glance"
            onClick={() => onAskAboutSurface("At a glance", "this week", null)}
          >
            <IrisSparkIcon size={16} />
          </button>
        </section>
      </div>
      <button type="button" className="pulse-v2-open-insights" onClick={onOpenInsights}>Open Insights <span>-&gt;</span></button>
    </div>
  );
};

const TOPIC_BAR_COLORS = ["#a868f5", "#eb3f9f", "#25a4b9", "#f6c445"];
// Insights "edit" mode widget keys for the non-KPI sections - hide-only (no
// reorder, they live in fixed CSS grid slots), stored in the same flat
// tracked_metrics list the KPI tiles use for their own visible+order state.
const INSIGHT_SECTION_KEYS = ["prescribed_moves", "performance_trends", "content_mix", "topic_performance", "audience_activity"] as const;
type InsightsLayout = { id: string; name: string; is_default: boolean; is_active: boolean; config: string[]; updated_at: string };

const AUDIENCE_HEAT_STEPS = ["#f2effa", "#ddd0f6", "#c2a7f0", "#a679ea", "#8a4fe1", "#6d2cd6"];
const heatColor = (value: number) => {
  const clamped = Math.max(0, Math.min(100, value));
  const idx = Math.min(AUDIENCE_HEAT_STEPS.length - 1, Math.floor((clamped / 100) * AUDIENCE_HEAT_STEPS.length));
  return AUDIENCE_HEAT_STEPS[idx];
};

const AudienceActivityHeatmap: React.FC<{ heatmap: NonNullable<DesignerMetrics["audience_activity_heatmap"]> }> = ({ heatmap }) => (
  <div className="pulse-v2-heatmap">
    <div className="pulse-v2-heatmap-grid" style={{ gridTemplateColumns: `32px repeat(${heatmap.hours.length}, 1fr)` }}>
      <div />
      {heatmap.hours.map((hour) => <div key={hour} className="pulse-v2-heatmap-hour">{hour}</div>)}
      {heatmap.days.map((day, rowIndex) => (
        <React.Fragment key={day}>
          <div className="pulse-v2-heatmap-day">{day}</div>
          {(heatmap.matrix[rowIndex] || []).map((value, colIndex) => (
            <div
              key={`${day}-${colIndex}`}
              className="pulse-v2-heatmap-cell"
              style={{ background: heatColor(value) }}
              title={`${day} ${heatmap.hours[colIndex] || ""}: ${value}% activity`}
            />
          ))}
        </React.Fragment>
      ))}
    </div>
    <div className="pulse-v2-heatmap-legend">
      <span>Low</span>
      {AUDIENCE_HEAT_STEPS.map((color) => <i key={color} style={{ background: color }} />)}
      <span>High</span>
    </div>
  </div>
);

export const PulseInsightsDashboard: React.FC<{
  range: PulseRange;
  onRangeChange: (value: PulseRange) => void;
  preferences: PulsePreferences;
  onPreferencesSave: (value: PulsePreferences) => Promise<void>;
  pinnedItems: DesignerSavedItem[];
  metrics: DesignerMetrics;
  status: Partial<Record<string, DesignerWidgetStatus>>;
  plan: DesignerPlan | null;
  planError?: string | null;
  typedInsights: DesignerTypedInsight[];
  typedInsightsLoading: boolean;
  account: Account;
  preview: DesignerPreview | null;
  onCommitMove: (item: DesignerPlanItem) => void;
  onCompleteMove: (item: DesignerPlanItem) => void;
  onAskInsight: (prompt: string) => void;
  onSaveInsight: (insight: DesignerTypedInsight) => void;
}> = ({ metrics, status, plan, planError, typedInsights, typedInsightsLoading, preview, onCommitMove, onCompleteMove, onAskInsight, onSaveInsight, range, onRangeChange, preferences, onPreferencesSave, pinnedItems }) => {
  const ready = (key: string) => status[key]?.ready === true && status[key]?.synthetic !== true;
  const chartMax = Math.max(1, ...metrics.performance_trends.flatMap((point) => [point.shortForm, point.longForm]));
  const actions = (plan?.items || []).slice(0, PLAN_VISIBLE_MOVES);
  const trendsReady = ready("performance_trends") && metrics.performance_trends.length > 1;
  // Content Mix / Topic Performance / Audience Activity render off the presence of real
  // values, not the backend's separate status.ready flag - that flag (and its synthetic
  // exclusion) is meant for the Pulse Score's stricter confidence gating, and was blocking
  // these 3 widgets from rendering even when the payload already carried real or demo data.
  const mixReady = Object.values(metrics.content_mix).some((value) => typeof value === "number" && value > 0);
  const topicsReady = metrics.topic_performance.length > 0;
  const audienceReady = metrics.audience_activity.length > 0;
  const topicMax = Math.max(1, ...metrics.topic_performance.map((topic) => topic.count));
  const mixShort = Math.max(0, Math.min(100, metrics.content_mix.short));
  const mixLong = Math.max(0, Math.min(100 - mixShort, metrics.content_mix.long));
  const mixCarousel = Math.max(0, Math.min(100 - mixShort - mixLong, metrics.content_mix.carousel));
  const period = metrics.period_metrics;
  const hasPeriod = Boolean(period?.sample_days);
  const deltaTrend = (value?: number): "up" | "down" | null => (value === undefined || value === null || !Number.isFinite(value) ? null : value >= 0 ? "up" : "down");
  const deltaNote = (value: number | undefined, fallback: string) => (value === undefined || value === null || !Number.isFinite(value) ? fallback : `${Math.abs(value).toFixed(1)}%`);
  // Pinned bookmarks split by type, matching the reference: a pinned Saved
  // Metric shows as an extra KPI tile in the top stat row; a pinned Saved
  // Action (not yet wired into Prescribed Moves - see PulsePage.tsx) keeps
  // showing in the feed below so it isn't silently lost.
  const pinnedMetrics = pinnedItems.filter((item) => item.type === "metric");
  const pinnedActions = pinnedItems.filter((item) => item.type !== "metric");
  const kpis: Array<{ key: string; label: string; value: string; note: string; trend: "up" | "down" | null }> = [
    { key: "views", label: "Views", value: hasPeriod ? compactNumber(period?.views) : "--", note: deltaNote(period?.views_delta_pct, "Selected period"), trend: deltaTrend(period?.views_delta_pct) },
    { key: "watch_hours", label: "Watch time", value: hasPeriod ? `${compactNumber(period?.watch_hours)} hrs` : "--", note: deltaNote(period?.watch_hours_delta_pct, "Selected period"), trend: deltaTrend(period?.watch_hours_delta_pct) },
    { key: "subscribers_net", label: "Subscribers", value: hasPeriod ? `${(period?.subscribers_net || 0) >= 0 ? "+" : ""}${period?.subscribers_net}` : "--", note: deltaNote(period?.subscribers_delta_pct, "Gained minus lost in selected period"), trend: deltaTrend(period?.subscribers_delta_pct) },
    { key: "engagement_rate", label: "Engagement rate", value: ready("engagement_rate") ? `${metrics.engagement_rate.value.toFixed(1)}%` : "--", note: metrics.engagement_rate.comparison_available ? `${Math.abs(metrics.engagement_rate.delta).toFixed(1)}% vs your average` : "No comparable earlier period", trend: metrics.engagement_rate.comparison_available ? (metrics.engagement_rate.delta >= 0 ? "up" : "down") : null },
    { key: "best_format", label: "Best format", value: ready("best_format") ? metrics.best_format.label : "--", note: "YouTube long-form scope", trend: null },
    { key: "optimal_time", label: "Optimal post time", value: ready("optimal_time") ? metrics.optimal_time.label : "Unavailable", note: ready("optimal_time") ? "Highest recent activity" : "Requires audience timing data", trend: ready("optimal_time") ? "up" : null },
  ];
  // Layout arrangement: one flat saved list (tracked_metrics) doubles as both the
  // visible-set AND the display order for KPI tiles - presence = visible, position =
  // order. Section widgets (prescribed_moves + the 4 chart cards) only use it for
  // visibility, not order, since they live in fixed CSS grid slots. A legacy saved
  // array that predates this feature (checkbox-only, KPI keys only) never contains any
  // section key - detect that and auto-append the section keys as visible so existing
  // users don't suddenly lose sections they never explicitly hid.
  const defaultKpiOrder = kpis.map((row) => row.key);
  const hasAnySectionKey = preferences.tracked_metrics?.some((key) => (INSIGHT_SECTION_KEYS as readonly string[]).includes(key));
  const trackedList = preferences.tracked_metrics
    ? (hasAnySectionKey ? preferences.tracked_metrics : [...preferences.tracked_metrics, ...INSIGHT_SECTION_KEYS])
    : [...defaultKpiOrder, ...INSIGHT_SECTION_KEYS];
  const isWidgetVisible = (key: string) => trackedList.includes(key);
  const kpiOrder = [
    ...trackedList.filter((key) => defaultKpiOrder.includes(key)),
    ...defaultKpiOrder.filter((key) => !trackedList.includes(key)),
  ];
  const [metricError, setMetricError] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const dragKeyRef = useRef<string | null>(null);

  // Named saved layouts (Default + user-created). Each layout's config is the
  // same flat widget-key array tracked_metrics already holds. The active
  // layout's config is kept in lockstep with tracked_metrics server-side
  // (PATCH/activate on it syncs PulsePreference.trackedMetrics), so editing
  // just PATCHes the active layout - reading still comes from `preferences`
  // as before, no separate render path for "layout mode" vs "legacy mode."
  const channelId = preview?.channel_id || "";
  const [layouts, setLayouts] = useState<InsightsLayout[]>([]);
  const [layoutsError, setLayoutsError] = useState("");
  const [pickerOpen, setPickerOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [saveAsOpen, setSaveAsOpen] = useState(false);
  const [saveAsNewExpanded, setSaveAsNewExpanded] = useState(false);
  const [newLayoutName, setNewLayoutName] = useState("");
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [renameDrafts, setRenameDrafts] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!channelId) return;
    let cancelled = false;
    pulseApi.get(`${API_BASE}/api/insights-layouts`, { params: { channel_id: channelId } })
      .then(({ data }) => { if (!cancelled) setLayouts(Array.isArray(data?.layouts) ? data.layouts : []); })
      .catch(() => { if (!cancelled) setLayoutsError("Could not load your saved layouts."); });
    return () => { cancelled = true; };
  }, [channelId]);

  const activeLayout = layouts.find((item) => item.is_active) || null;

  const saveLayout = async (nextTrackedList: string[]) => {
    try {
      if (activeLayout) {
        const { data } = await pulseApi.patch(`${API_BASE}/api/insights-layouts/${activeLayout.id}`, { config: nextTrackedList });
        setLayouts((prev) => prev.map((item) => (item.id === activeLayout.id ? data : item)));
      }
      await onPreferencesSave({ tracked_metrics: nextTrackedList });
      setMetricError("");
    } catch {
      setMetricError("Could not save your layout.");
    }
  };
  const activateLayout = async (id: string) => {
    try {
      const { data } = await pulseApi.post(`${API_BASE}/api/insights-layouts/${id}/activate`, {});
      setLayouts((prev) => prev.map((item) => ({ ...item, is_active: item.id === data.id })));
      await onPreferencesSave({ tracked_metrics: data.config });
      setLayoutsError("");
    } catch {
      setLayoutsError("Could not switch layouts.");
    }
  };
  const createLayout = async (name: string, config?: string[]) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const { data } = await pulseApi.post(`${API_BASE}/api/insights-layouts`, { channel_id: channelId, name: trimmed, config: config || trackedList });
      setLayouts((prev) => [...prev.map((item) => ({ ...item, is_active: false })), data]);
      await onPreferencesSave({ tracked_metrics: data.config });
      setLayoutsError("");
      setNewLayoutName("");
      setSaveAsNewExpanded(false);
      setSaveAsOpen(false);
    } catch (err: any) {
      setLayoutsError(err?.response?.status === 409 ? "A layout with that name already exists." : "Could not create that layout.");
    }
  };
  const renameLayout = async (id: string, name: string) => {
    const trimmed = name.trim();
    if (!trimmed) return;
    try {
      const { data } = await pulseApi.patch(`${API_BASE}/api/insights-layouts/${id}`, { name: trimmed });
      setLayouts((prev) => prev.map((item) => (item.id === id ? data : item)));
      setLayoutsError("");
    } catch {
      setLayoutsError("Could not rename that layout.");
    }
  };
  const duplicateLayout = (layout: InsightsLayout) => createLayout(`${layout.name} copy`, layout.config);
  const saveCurrentInto = async (layout: InsightsLayout) => {
    try {
      const { data } = await pulseApi.patch(`${API_BASE}/api/insights-layouts/${layout.id}`, { config: trackedList });
      let nextLayouts = layouts.map((item) => (item.id === layout.id ? data : item));
      if (!layout.is_active) {
        const { data: activated } = await pulseApi.post(`${API_BASE}/api/insights-layouts/${layout.id}/activate`, {});
        nextLayouts = nextLayouts.map((item) => ({ ...item, is_active: item.id === layout.id }));
        await onPreferencesSave({ tracked_metrics: activated.config });
      }
      setLayouts(nextLayouts);
      setLayoutsError("");
    } catch {
      setLayoutsError("Could not save into that layout.");
    }
    setSaveAsOpen(false);
  };
  const deleteLayout = async (id: string) => {
    try {
      await pulseApi.delete(`${API_BASE}/api/insights-layouts/${id}`);
      const remaining = layouts.filter((item) => item.id !== id);
      const wasActive = layouts.find((item) => item.id === id)?.is_active;
      const fallback = remaining.find((item) => item.is_default) || remaining[0] || null;
      setLayouts(wasActive && fallback ? remaining.map((item) => ({ ...item, is_active: item.id === fallback.id })) : remaining);
      if (wasActive && fallback) await onPreferencesSave({ tracked_metrics: fallback.config });
      setConfirmDeleteId(null);
      setLayoutsError("");
    } catch {
      setLayoutsError("Could not delete that layout.");
    }
  };
  const toggleWidgetVisible = (key: string) => saveLayout(isWidgetVisible(key) ? trackedList.filter((item) => item !== key) : [...trackedList, key]);
  const reorderKpi = (fromKey: string, toKey: string) => {
    if (fromKey === toKey) return;
    const next = kpiOrder.slice();
    const fromIdx = next.indexOf(fromKey);
    const toIdx = next.indexOf(toKey);
    if (fromIdx === -1 || toIdx === -1) return;
    next.splice(fromIdx, 1);
    next.splice(toIdx, 0, fromKey);
    const visibleKpisInOrder = next.filter((key) => isWidgetVisible(key));
    const sectionKeysInOrder = INSIGHT_SECTION_KEYS.filter((key) => isWidgetVisible(key));
    saveLayout([...visibleKpisInOrder, ...sectionKeysInOrder]);
  };
  const askAbout = (label: string) => onAskInsight(`What's driving ${label} right now? Use only measured data, and say plainly if there isn't enough yet.`);
  // Hide-only chrome for the 5 section widgets (Prescribed moves + the 4 chart
  // cards) - they live in fixed CSS grid slots so, unlike the KPI tiles above,
  // this doesn't offer drag-to-reorder, only the same visibility toggle plus an
  // "Ask IRIS" button in view mode.
  const widgetHideToggle = (widgetKey: typeof INSIGHT_SECTION_KEYS[number], label: string) => {
    const hidden = !isWidgetVisible(widgetKey);
    return (
      <button type="button" className="pulse-v2-widget-inline-toggle" aria-label={hidden ? `Show ${label}` : `Hide ${label}`} onClick={() => toggleWidgetVisible(widgetKey)}>
        {hidden ? <VisibilityOffRoundedIcon fontSize="inherit" /> : <VisibilityOutlinedIcon fontSize="inherit" />}
      </button>
    );
  };
  const SectionWidget: React.FC<{ widgetKey: typeof INSIGHT_SECTION_KEYS[number]; label: string; askable?: boolean; overlayControl?: boolean; className: string; children: React.ReactNode }> = ({ widgetKey, label, askable = true, overlayControl = true, className, children }) => {
    const hidden = !isWidgetVisible(widgetKey);
    if (hidden && !editOpen) return null;
    return (
      <article className={`${className}${hidden ? " pulse-v2-insight-widget-hidden" : ""}`}>
        {editOpen && overlayControl ? (
          <div className="pulse-v2-widget-controls">{widgetHideToggle(widgetKey, label)}</div>
        ) : !editOpen && askable ? (
          <button type="button" className="pulse-v2-widget-ask" aria-label={`Ask IRIS about ${label}`} onClick={() => askAbout(label)}><IrisSparkIcon size={16} /></button>
        ) : null}
        {children}
      </article>
    );
  };
  return (
    <div className="pulse-v2-page pulse-v2-insights">
      <header className="pulse-v2-page-head">
        <div>
          <div className="pulse-v2-insights-title-row"><h1>Insights</h1><span className="pulse-v2-platform-pill"><YouTubeIcon fontSize="inherit" /> YouTube - Videos</span></div>
          <p>{metrics.header.subtitle}</p>
        </div>
        <div className="pulse-v2-insights-head-actions">
          {editOpen ? (
            <>
              <button type="button" className="pulse-v2-outline-action pulse-v2-text-action" onClick={() => setEditOpen(false)}>Clear</button>
              <button type="button" className="pulse-v2-outline-action" onClick={() => { setSaveAsOpen(true); setSaveAsNewExpanded(false); setNewLayoutName(""); }}><SaveOutlinedIcon fontSize="small" /> Save Layout</button>
            </>
          ) : (
            <button type="button" className="pulse-v2-outline-action" onClick={() => setEditOpen(true)}>Edit</button>
          )}
          <button type="button" className="pulse-v2-primary" onClick={() => onAskInsight("Find one measured metric worth saving to Bookmark and explain why it matters.")}><AddRoundedIcon fontSize="small" /> Add Bookmark</button>
        </div>
      </header>
      <div className="pulse-v2-insights-toolbar">
        <div className="pulse-v2-layout-picker">
          <button type="button" className="pulse-v2-layout-picker-trigger" onClick={() => setPickerOpen((value) => !value)} aria-expanded={pickerOpen}>
            <GridViewRoundedIcon fontSize="inherit" />
            <span>{activeLayout?.name || "Default"}</span>
            <KeyboardArrowDownRoundedIcon fontSize="inherit" />
          </button>
          {pickerOpen && (
            <div className="pulse-v2-layout-dropdown" role="menu">
              {layouts.length === 0 ? <div className="pulse-v2-layout-dropdown-row pulse-v2-layout-dropdown-empty">Default</div> : layouts.map((layout) => (
                <button
                  type="button"
                  key={layout.id}
                  className="pulse-v2-layout-dropdown-row"
                  onClick={() => { setPickerOpen(false); if (!layout.is_active) activateLayout(layout.id); }}
                >
                  {layout.is_default ? <LockRoundedIcon fontSize="inherit" /> : layout.is_active ? <CheckRoundedIcon fontSize="inherit" /> : <span className="pulse-v2-layout-dropdown-spacer" />}
                  <span>{layout.name}</span>
                </button>
              ))}
              <div className="pulse-v2-layout-dropdown-divider" />
              <button type="button" className="pulse-v2-layout-dropdown-row pulse-v2-layout-dropdown-manage" onClick={() => { setPickerOpen(false); setManageOpen(true); }}><TuneRoundedIcon fontSize="inherit" /><span>Manage layouts...</span></button>
            </div>
          )}
        </div>
        <RangePicker value={range} onChange={onRangeChange} />
      </div>
      {metricError ? <p role="status" className="pulse-v2-widget-error">{metricError}</p> : null}
      {layoutsError ? <p role="status" className="pulse-v2-widget-error">{layoutsError}</p> : null}
      {saveAsOpen && (
        <div className="pulse-v2-modal-backdrop" role="dialog" aria-modal="true">
          <div className="pulse-v2-edit-dialog pulse-v2-layout-dialog">
            <header><div><h2>Save layout</h2><p>Save your changes to an existing layout, or create a new one. The Default layout can&apos;t be changed.</p></div><button type="button" onClick={() => { setSaveAsOpen(false); setSaveAsNewExpanded(false); }} aria-label="Close">&times;</button></header>
            <div className="pulse-v2-edit-body">
              <div className="pulse-v2-layout-modal-row pulse-v2-layout-modal-locked"><span>Default</span><span className="pulse-v2-layout-locked-pill"><LockRoundedIcon fontSize="inherit" /> Locked</span></div>
              {layouts.filter((layout) => !layout.is_default).map((layout) => (
                <button type="button" key={layout.id} className="pulse-v2-layout-modal-row pulse-v2-layout-modal-select" onClick={() => saveCurrentInto(layout)}>
                  <span>{layout.name}</span>
                </button>
              ))}
              <button type="button" className={`pulse-v2-layout-modal-row pulse-v2-layout-modal-new${saveAsNewExpanded ? " active" : ""}`} onClick={() => setSaveAsNewExpanded(true)}>
                <AddRoundedIcon fontSize="small" /> Save as new layout
              </button>
              {saveAsNewExpanded ? (
                <input
                  autoFocus
                  className="pulse-v2-layout-modal-input"
                  placeholder="Layout name"
                  value={newLayoutName}
                  onChange={(event) => setNewLayoutName(event.target.value)}
                />
              ) : null}
              <div className="pulse-v2-layout-modal-actions">
                <button type="button" className="pulse-v2-outline-action" onClick={() => { setSaveAsOpen(false); setSaveAsNewExpanded(false); }}>Cancel</button>
                <button type="button" className="pulse-v2-primary" disabled={!newLayoutName.trim()} onClick={() => createLayout(newLayoutName)}>Create layout</button>
              </div>
            </div>
          </div>
        </div>
      )}
      {manageOpen && (
        <div className="pulse-v2-modal-backdrop" role="dialog" aria-modal="true">
          <div className="pulse-v2-edit-dialog pulse-v2-layout-dialog">
            <header><div><h2>Manage layouts</h2><p>Rename, duplicate or delete your saved layouts.</p></div><button type="button" onClick={() => { setManageOpen(false); setConfirmDeleteId(null); }} aria-label="Close">&times;</button></header>
            <div className="pulse-v2-edit-body">
              <div className="pulse-v2-layout-modal-row pulse-v2-layout-modal-locked"><span>Default</span><span className="pulse-v2-layout-locked-pill"><LockRoundedIcon fontSize="inherit" /> Locked</span></div>
              {layouts.filter((layout) => !layout.is_default).length === 0 ? (
                <p className="pulse-v2-layout-modal-empty">No custom layouts yet. Use Edit → Save Layout to create one.</p>
              ) : layouts.filter((layout) => !layout.is_default).map((layout) => (
                confirmDeleteId === layout.id ? (
                  <div className="pulse-v2-layout-modal-row pulse-v2-layout-modal-confirm" key={layout.id}>
                    <span>Delete &ldquo;{layout.name}&rdquo;?</span>
                    <span className="pulse-v2-layout-modal-confirm-actions">
                      <button type="button" className="pulse-v2-outline-action" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                      <button type="button" className="pulse-v2-layout-delete-confirm" onClick={() => deleteLayout(layout.id)}>Delete</button>
                    </span>
                  </div>
                ) : (
                  <div className="pulse-v2-layout-modal-row pulse-v2-layout-modal-edit" key={layout.id}>
                    <input
                      className="pulse-v2-layout-modal-input"
                      value={renameDrafts[layout.id] ?? layout.name}
                      onChange={(event) => setRenameDrafts((prev) => ({ ...prev, [layout.id]: event.target.value }))}
                      onBlur={() => { const next = (renameDrafts[layout.id] ?? layout.name).trim(); if (next && next !== layout.name) renameLayout(layout.id, next); }}
                    />
                    <button type="button" aria-label={`Duplicate ${layout.name}`} onClick={() => duplicateLayout(layout)}><ContentCopyRoundedIcon fontSize="inherit" /></button>
                    <button type="button" aria-label={`Delete ${layout.name}`} onClick={() => setConfirmDeleteId(layout.id)}><DeleteOutlineRoundedIcon fontSize="inherit" /></button>
                  </div>
                )
              ))}
              <div className="pulse-v2-layout-modal-actions">
                <button type="button" className="pulse-v2-primary" onClick={() => { setManageOpen(false); setConfirmDeleteId(null); }}>Done</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <section className="pulse-v2-kpi-grid">
        {(editOpen ? kpiOrder : kpiOrder.filter((key) => isWidgetVisible(key))).map((key) => {
          const row = kpis.find((item) => item.key === key);
          if (!row) return null;
          const { label, value, note, trend } = row;
          const hidden = !isWidgetVisible(key);
          return (
            <article
              key={key}
              className={hidden ? "pulse-v2-insight-widget-hidden" : undefined}
              draggable={editOpen}
              onDragStart={() => { dragKeyRef.current = key; }}
              onDragOver={(event) => { if (editOpen) event.preventDefault(); }}
              onDrop={() => { if (dragKeyRef.current) { reorderKpi(dragKeyRef.current, key); dragKeyRef.current = null; } }}
            >
              {editOpen ? (
                <div className="pulse-v2-widget-controls">
                  <span className="pulse-v2-widget-drag" aria-label={`Move ${label}`}><DragIndicatorRoundedIcon fontSize="inherit" /></span>
                  <button type="button" aria-label={hidden ? `Show ${label}` : `Hide ${label}`} onClick={() => toggleWidgetVisible(key)}>
                    {hidden ? <VisibilityOffRoundedIcon fontSize="inherit" /> : <VisibilityOutlinedIcon fontSize="inherit" />}
                  </button>
                </div>
              ) : (
                <button type="button" className="pulse-v2-widget-ask" aria-label={`Ask IRIS about ${label}`} onClick={() => askAbout(label)}><IrisSparkIcon size={16} /></button>
              )}
              <span>{label}</span>
              <strong>{value}</strong>
              <small className={trend ? `pulse-v2-kpi-trend ${trend}` : ""}>{trend === "up" ? "↗ " : trend === "down" ? "↘ " : ""}{note}</small>
            </article>
          );
        })}
        {pinnedMetrics.map((item) => (
          <article key={`pinned-${item.id}`} className="pulse-v2-kpi-pinned">
            <span className="pulse-v2-kpi-bookmark-badge" aria-label="Pinned from Bookmark"><BookmarkRoundedIcon fontSize="inherit" /></span>
            <span>{item.category || "Bookmark"}</span>
            <strong>{item.value || item.title}</strong>
            <small>{item.title}</small>
          </article>
        ))}
      </section>
      {pinnedActions.length > 0 && <section className="pulse-v2-typed-grid">{pinnedActions.map((item) => <article className="pulse-v2-card" key={item.id}><span className="pulse-v2-eyebrow">Pinned bookmark</span><h3>{item.title}</h3><ReactMarkdown remarkPlugins={[remarkGfm]}>{item.body}</ReactMarkdown></article>)}</section>}

      <SectionWidget widgetKey="prescribed_moves" label="Prescribed moves" askable={false} overlayControl={false} className="pulse-v2-card pulse-v2-prescribed">
        <div className="pulse-v2-card-topline"><div><span className="pulse-v2-eyebrow">This week's moves</span><h2>Prescribed moves</h2></div><div className="pulse-v2-card-topline-right"><span>{actions.filter((item) => item.status === "verified").length} of {actions.length || 0} verified</span>{editOpen ? widgetHideToggle("prescribed_moves", "Prescribed moves") : null}</div></div>
        <p>Commit a move and IRIS watches the metric. It is done only when the result actually moves, not when you check a box.</p>
        {planError ? <div className="pulse-v2-action-message error">{planError}</div> : null}
        <div className="pulse-v2-prescribed-list">
          {actions.length ? actions.map((item, index) => {
            const isManual = item.origin === "manual";
            const rowClass = item.status === "verified"
              ? "verified"
              : isManual
                ? (item.status === "done" ? "manual-done" : "manual-pending")
                : item.status === "pending" ? "progress" : item.status;
            // A manual move has no metric to measure - it only ever earns the
            // (non-green) checkmark once the user self-reports it done, never
            // the algorithmic in-progress spinner.
            const icon = item.status === "verified" || (isManual && item.status === "done")
              ? <CheckRoundedIcon fontSize="inherit" />
              : item.status === "pending" && !isManual
                ? <AutoAwesomeRoundedIcon fontSize="inherit" />
                : item.status === "no_move"
                  ? <TrendingFlatRoundedIcon fontSize="inherit" />
                  : item.status === "expired"
                    ? <HelpOutlineRoundedIcon fontSize="inherit" />
                    : null;
            return (
              <article className={rowClass} key={item.id || `${item.title}-${index}`}>
                <span>{icon}</span>
                <div><strong>{item.title}</strong><em>{isManual ? "Manual" : (item.pillar_label || "Measured move")}</em><p>{item.detail}</p><small>{moveStatusLabel(item)}</small>{item.evidence ? <p className="pulse-v2-move-evidence">{item.evidence}</p> : null}</div>
                {item.status === "proposed" ? <button type="button" onClick={() => onCommitMove(item)}>Commit</button> : null}
                {!isManual && (item.status === "no_move" || item.status === "expired") ? <button type="button" onClick={() => onCommitMove(item)}>Try again</button> : null}
                {isManual && item.status === "pending" ? <button type="button" onClick={() => onCompleteMove(item)}>Mark done</button> : null}
              </article>
            );
          }) : <EmptyMetric label="IRIS is preparing prescribed moves" />}
        </div>
      </SectionWidget>

      {(editOpen || isWidgetVisible("performance_trends") || isWidgetVisible("content_mix")) && (
      <section className="pulse-v2-insight-chart-grid pulse-v2-insight-chart-grid-top">
        <SectionWidget widgetKey="performance_trends" label="Performance Trends" className="pulse-v2-card pulse-v2-insight-chart-card pulse-v2-trends-card">
          <header><h2>Performance Trends</h2><p>Engagement rate by content format</p></header>
          {trendsReady
            ? <InsightsTrendChart points={metrics.performance_trends} />
            : <InsightsPending status={status.performance_trends} fallback="Trend lines need at least two measured periods" />}
        </SectionWidget>

        <SectionWidget widgetKey="content_mix" label="Content Mix" className="pulse-v2-card pulse-v2-insight-chart-card pulse-v2-mix-card">
          <header><h2>Content Mix</h2><p>Format distribution</p></header>
          {mixReady ? (
            <div className="pulse-v2-mix-chart">
              <div
                className="pulse-v2-mix-donut"
                style={{
                  background: `conic-gradient(#a868f5 0 ${mixShort}%, #eb3f9f ${mixShort}% ${mixShort + mixLong}%, #25a4b9 ${mixShort + mixLong}% ${mixShort + mixLong + mixCarousel}%, #e8e5ef ${mixShort + mixLong + mixCarousel}% 100%)`,
                }}
              ><span><strong>{metrics.content_mix.total_videos ?? "--"}</strong><small>videos</small></span></div>
              <div className="pulse-v2-mix-legend">
                <div><span><i className="short" />Short-form</span><strong>{mixShort}%</strong></div>
                <div><span><i className="long" />Long-form</span><strong>{mixLong}%</strong></div>
                <div><span><i className="carousel" />Carousel</span><strong>{mixCarousel}%</strong></div>
              </div>
            </div>
          ) : <InsightsPending status={status.content_mix} fallback="Format distribution is not reliable yet" />}
        </SectionWidget>
      </section>
      )}

      {(editOpen || isWidgetVisible("topic_performance") || isWidgetVisible("audience_activity")) && (
      <section className="pulse-v2-insight-chart-grid pulse-v2-insight-chart-grid-bottom">
        <SectionWidget widgetKey="topic_performance" label="Topic Performance" className="pulse-v2-card pulse-v2-insight-chart-card pulse-v2-topic-card">
          <header><h2>Topic Performance</h2><p>Title clusters by upload count - {metrics.topic_window || "last 28 days"}</p></header>
          {topicsReady ? (
            <div className="pulse-v2-topic-bars">
              {metrics.topic_performance.slice(0, 5).map((topic, index) => (
                <div key={`${topic.label}-${index}`}>
                  <span>{topic.label}</span>
                  <i><b style={{ width: `${Math.max(4, (topic.count / topicMax) * 100)}%`, background: TOPIC_BAR_COLORS[index % TOPIC_BAR_COLORS.length] }} /></i>
                  <strong>{topic.count}</strong>
                </div>
              ))}
            </div>
          ) : <InsightsPending status={status.topic_performance} fallback="Topic-level performance needs more real upload data" />}
        </SectionWidget>

        <SectionWidget widgetKey="audience_activity" label="Audience Activity" className="pulse-v2-card pulse-v2-insight-chart-card pulse-v2-activity-card">
          <header><h2>Audience Activity</h2><p>When your audience is most active</p></header>
          {audienceReady ? (
            metrics.audience_activity_heatmap?.matrix?.length ? (
              <AudienceActivityHeatmap heatmap={metrics.audience_activity_heatmap} />
            ) : (
              <>
                <div className="pulse-v2-activity-chart">
                  {metrics.audience_activity.slice(0, 7).map((value, index) => {
                    const detail = metrics.audience_activity_detail?.[index];
                    return (
                      <div key={`${detail?.label || "window"}-${index}`} title={detail?.avg_views ? `${detail.avg_views} average views` : `${value}% activity`}>
                        <span style={{ height: `${Math.max(5, Math.min(100, value))}%` }} />
                        <small>{detail?.label || `W${index + 1}`}</small>
                      </div>
                    );
                  })}
                </div>
                <p className="pulse-v2-heatmap-pending-note">Hour-of-day detail isn't exposed by the YouTube Analytics API for this channel - showing daily activity only.</p>
              </>
            )
          ) : <InsightsPending status={status.audience_activity} fallback="Audience activity is waiting for analytics-backed timing data" />}
        </SectionWidget>
      </section>
      )}

      <section className="pulse-v2-insight-feed">
        <div className="pulse-v2-card-topline"><div><span className="pulse-v2-eyebrow">IRIS signal feed</span><h2>What deserves attention</h2></div><span>YouTube long-form</span></div>
        {typedInsightsLoading ? <div className="pulse-v2-card pulse-v2-empty"><p>Reading the latest measured snapshot...</p></div> : !typedInsights.length ? <div className="pulse-v2-card"><p>No reliable insights are available for this period yet. You can still ask IRIS to help plan your next upload.</p><button type="button" onClick={() => onAskInsight("Help me plan my next upload using the data available. Be explicit about any missing evidence.")}>Ask IRIS</button></div> : (
          <div className="pulse-v2-typed-grid">
            {typedInsights.map((insight) => (
              <article className={`pulse-v2-card pulse-v2-typed ${insight.type}`} key={insight.id}>
                <div><em>{insight.type_label}</em><span>{insight.confidence} confidence</span></div>
                <h3>{insight.title}</h3>
                <p>{insight.body}</p>
                <small>Evidence: {insight.evidence} - {insight.surface}</small>
                <footer><button type="button" onClick={() => onAskInsight(`${insight.action_prompt} Turn this into one measurable move I can commit.`)}>Act</button><button type="button" onClick={() => onAskInsight(insight.action_prompt)}>Ask IRIS</button><button type="button" onClick={() => onSaveInsight(insight)}><BookmarkBorderRoundedIcon fontSize="small" /> Save</button></footer>
              </article>
            ))}
          </div>
        )}
      </section>

      <section className="pulse-v2-card pulse-v2-performance-bars">
        <h2>Engagement rate - recent periods</h2>
        {trendsReady ? <div className="pulse-v2-bars">
          {metrics.performance_trends.map((point) => (
            <div key={point.label} title={`${point.label}: ${point.longForm}% engagement`}><span style={{ height: `${Math.max(0, (Math.max(point.shortForm, point.longForm) / chartMax) * 100)}%` }} /><small>{point.label}</small></div>
          ))}
        </div> : <InsightsPending status={status.performance_trends} fallback="Performance history needs more measured snapshots" />}
        <p>This chart reflects connected-channel performance only. Missing periods are never estimated.</p>
      </section>
    </div>
  );
};

export const PulseScoreDashboard: React.FC<{
  preferences: PulsePreferences;
  metrics: DesignerMetrics;
  scoreStatus?: DesignerWidgetStatus;
  channelDataState?: "ready" | "low_data" | "no_data";
  scoreLoading?: boolean;
  oauthConnected: boolean;
  preview: DesignerPreview | null;
  onAskAboutSurface: (label: string, shortLabel: string, score: number) => void;
}> = ({ metrics, scoreStatus, channelDataState, scoreLoading, oauthConnected, preview, preferences, onAskAboutSurface }) => {
  const score = scoreValue(metrics.pulse_score.score);
  const scoreReady = hasRealScore(metrics.pulse_score) && scoreStatus?.synthetic !== true;
  const samples = scoreStatus?.sample_days ?? metrics.pulse_score.sample_days ?? 0;
  const target = scoreStatus?.min_sample_days ?? metrics.pulse_score.min_sample_days ?? 21;
  const shorts = metrics.shorts_score;
  const shortsScore = scoreValue(shorts?.score);
  const shortsReady = hasRealScore(shorts) && scoreStatus?.synthetic !== true;
  const videosInsufficient = metrics.pulse_score.score_state === "insufficient_data";
  const shortsInsufficient = shorts?.score_state === "insufficient_data";
  const shortsConfidence = !shortsReady ? "Pending" : shorts?.score_state === "provisional" ? "Provisional" : (shorts?.confidence || "low");
  const [platform, setPlatform] = useState<"videos" | "shorts">("videos");
  const activeConfidence = platform === "videos" ? scoreConfidence(metrics) : shortsConfidence;
  const activeReady = platform === "videos" ? scoreReady : shortsReady;
  const activeWeightedSubscores = platform === "videos" ? metrics.pulse_score.weighted_subscores : shorts?.weighted_subscores;
  const [trendOpen, setTrendOpen] = useState(false);
  // The trend modal follows whichever surface tab is selected, same as the breakdown card
  // just above already does (activeWeightedSubscores) - Shorts' own weekly_trend, not Video's,
  // so a Shorts-only channel gets its own real trend instead of an always-empty Video one.
  const weeklyTrend = platform === "videos" ? metrics.pulse_score.weekly_trend : shorts?.weekly_trend;
  // Backend sends score: null for a week that was genuinely insufficient_data as of its own end
  // date - honest gap, never a fabricated/interpolated number. "Current score" and the delta use
  // the first/last REAL (non-null) weeks, not weeklyTrend[0]/weeklyTrend[length-1] directly, which
  // could themselves be null gaps.
  const weeklyTrendReady = weeklyTrend?.filter((point): point is { week_start: string; week_end: string; score: number } => point.score !== null);
  const milestonePct = scoreReady ? Math.round(score * 10) : 0;
  const irisConfidenceCopy: Record<string, string> = {
    high: "This score carries high confidence - above materiality, with strong sponsor-ready evidence behind it.",
    medium: "This score carries medium confidence - directionally reliable, but at least one pillar still needs more measured evidence.",
    low: "This score carries low confidence - treat it as an early read until more measured evidence comes in.",
    provisional: "This is a provisional score - a real early read from fewer measured days than an established score needs. It will firm up as more data comes in.",
    pending: "IRIS is still gathering measured evidence for this score - a confidence read will appear once enough data is collected.",
  };
  return (
    <div className="pulse-v2-page pulse-v2-score-page">
      <header className="pulse-v2-page-head">
        <div><h1>Pulse Score</h1><p>Your on-demand video score - built for persistent, searchable, long-tail content. This is not a generic "YouTube score."</p></div>
        <button type="button" className="pulse-v2-outline-action" onClick={() => setTrendOpen((value) => !value)} aria-expanded={trendOpen}><ShowChartRoundedIcon fontSize="small" /> View 7-week trend</button>
      </header>
      {trendOpen && (
        <div className="pulse-v2-modal-backdrop" role="dialog" aria-modal="true">
          <div className="pulse-v2-edit-dialog pulse-v2-trend-dialog">
            <header>
              <div><h2>Pulse Score ({platform === "shorts" ? "Shorts" : "Videos"}) · 7-week trend</h2><p>How your readiness score has moved, and what&apos;s driving it now.</p></div>
              <button type="button" onClick={() => setTrendOpen(false)} aria-label="Close">&times;</button>
            </header>
            <div className="pulse-v2-edit-body">
              {weeklyTrend && weeklyTrend.length > 1 && weeklyTrendReady && weeklyTrendReady.length > 0 ? (
                <>
                  <div className="pulse-v2-card pulse-v2-trend-current">
                    <div className="pulse-v2-trend-current-left">
                      <span>Current score</span>
                      <strong>{weeklyTrendReady[weeklyTrendReady.length - 1].score.toFixed(1)}</strong>
                    </div>
                    {weeklyTrendReady.length > 1 && weeklyTrendReady[0].score ? (
                      <em className={weeklyTrendReady[weeklyTrendReady.length - 1].score >= weeklyTrendReady[0].score ? "up" : "down"}>
                        {weeklyTrendReady[weeklyTrendReady.length - 1].score >= weeklyTrendReady[0].score ? <TrendingUpRoundedIcon fontSize="inherit" /> : <TrendingDownRoundedIcon fontSize="inherit" />}
                        {weeklyTrendReady[weeklyTrendReady.length - 1].score >= weeklyTrendReady[0].score ? "+" : ""}
                        {(((weeklyTrendReady[weeklyTrendReady.length - 1].score - weeklyTrendReady[0].score) / weeklyTrendReady[0].score) * 100).toFixed(0)}% from last period
                      </em>
                    ) : null}
                  </div>
                  <div className="pulse-v2-card pulse-v2-trend-chart-card">
                    <ScoreTrendChart
                      points={weeklyTrend.map((point) => ({
                        label: new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(new Date(point.week_end)),
                        score: point.score,
                      }))}
                    />
                  </div>
                  <div className="pulse-v2-card pulse-v2-score-breakdown-card">
                    <h2>Score breakdown</h2>
                    <ScoreBreakdown weightedSubscores={activeWeightedSubscores} ready={activeReady} confidence={activeConfidence} showConfidencePills={false} />
                  </div>
                </>
              ) : (
                <p>A 7-week score trend needs weekly historical snapshots that aren't being sent yet - shown honestly as unavailable instead of a fabricated line.</p>
              )}
            </div>
          </div>
        </div>
      )}
      <div className="pulse-v2-firewall"><strong>The firewall:</strong> no in-app behavior moves your score. No connection or materiality event moves it. Your score changes only when your measured YouTube performance changes.</div>
      {oauthConnected && scoreLoading ? (
        <div className="pulse-v2-card pulse-v2-no-data-card pending">
          <NoDataGaugeIcon size={64} />
          <h2>Calculating your score...</h2>
          <p>IRIS is weighing content strategy, audience growth, execution craft, and publishing discipline for this channel.</p>
        </div>
      ) : channelDataState === "no_data" ? (
        <div className="pulse-v2-card pulse-v2-no-data-card">
          <NoDataGaugeIcon size={64} />
          <h2>Not enough data to score yet</h2>
          <p>{notEnoughDataCopy("", metrics.pulse_score)}</p>
        </div>
      ) : (
        <>
          <div className="pulse-v2-score-cards">
            <article
              className={`pulse-v2-card pulse-v2-score-card-select ${videosInsufficient ? "pulse-v2-score-card-no-data" : ""} ${platform === "videos" ? "selected" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => setPlatform("videos")}
              onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && setPlatform("videos")}
            >
              {videosInsufficient ? <NoDataGaugeIcon size={48} /> : <PulseSemicircleGauge score={score} pending={!scoreReady} gradientId="pulseScoreGaugeVideos" />}
              <div className="pulse-v2-score-card-info">
                <div className="pulse-v2-score-card-head"><span className="pulse-v2-score-platform-icon"><YouTubeIcon fontSize="inherit" /></span><strong>YouTube · Videos</strong></div>
                {videosInsufficient ? (
                  <>
                    <strong className="pulse-v2-no-data-title">Not enough data to score yet</strong>
                    <small>{notEnoughDataCopy("video", metrics.pulse_score)}</small>
                  </>
                ) : (
                  <>
                    <em className={scoreConfidence(metrics).toLowerCase()}>{scoreConfidence(metrics)}</em>
                    <small>{metrics.pulse_score.video_sample_count ? `${metrics.pulse_score.video_sample_count} videos read` : `${samples} daily snapshots read`}</small>
                  </>
                )}
              </div>
              <button
                type="button"
                className="pulse-v2-score-card-iris-jump"
                aria-label="Ask IRIS about YouTube Videos"
                onClick={(event) => { event.stopPropagation(); onAskAboutSurface("YouTube - Videos", "Videos", score); }}
              >
                <IrisSparkIcon size={16} />
              </button>
            </article>
            <article
              className={`pulse-v2-card pulse-v2-score-card-select ${shortsInsufficient ? "pulse-v2-score-card-no-data" : ""} ${platform === "shorts" ? "selected" : ""}`}
              role="button"
              tabIndex={0}
              onClick={() => setPlatform("shorts")}
              onKeyDown={(event) => (event.key === "Enter" || event.key === " ") && setPlatform("shorts")}
            >
              {shortsInsufficient ? <NoDataGaugeIcon size={48} /> : <PulseSemicircleGauge score={shortsScore} pending={!shortsReady} gradientId="pulseScoreGaugeShorts" />}
              <div className="pulse-v2-score-card-info">
                <div className="pulse-v2-score-card-head"><span className="pulse-v2-score-platform-icon"><YouTubeIcon fontSize="inherit" /></span><strong>YouTube · Shorts</strong></div>
                {shortsInsufficient ? (
                  <>
                    <strong className="pulse-v2-no-data-title">Not enough data to score yet</strong>
                    <small>{notEnoughDataCopy("shorts", shorts)}</small>
                  </>
                ) : (
                  <>
                    <em className={shortsConfidence.toLowerCase()}>{shortsConfidence}</em>
                    <small>{shorts?.shorts_video_count ? `${shorts.shorts_video_count} shorts read` : "Waiting on measured Shorts analytics"}</small>
                  </>
                )}
              </div>
              <button
                type="button"
                className="pulse-v2-score-card-iris-jump"
                aria-label="Ask IRIS about YouTube Shorts"
                onClick={(event) => { event.stopPropagation(); onAskAboutSurface("YouTube - Shorts", "Shorts", shortsScore); }}
              >
                <IrisSparkIcon size={16} />
              </button>
            </article>
          </div>

          <div className="pulse-v2-card pulse-v2-iris-confidence-line"><span className="pulse-v2-iris-confidence-badge"><AutoAwesomeRoundedIcon fontSize="inherit" /></span><p><strong>IRIS:</strong> {irisConfidenceCopy[activeReady ? activeConfidence.toLowerCase() : "pending"] || irisConfidenceCopy.pending}</p></div>

          <section className="pulse-v2-card pulse-v2-score-breakdown-card pulse-v2-iris-jump-host">
            <h2>Breakdown</h2>
            <ScoreBreakdown weightedSubscores={activeWeightedSubscores} ready={activeReady} confidence={activeConfidence} />
            <p>Weights: Content Strategy 30%, Audience Growth &amp; Retention 30%, Execution Craft 25%, Publishing Discipline 15%. Missing evidence remains pending instead of being filled with synthetic data.</p>
        <p>A pillar at 25% weight or more sitting at Low confidence caps this channel's overall confidence at Medium, regardless of the others.</p>
        <button
          type="button"
          className="pulse-v2-score-card-iris-jump"
          aria-label={`Ask IRIS about the ${platform === "videos" ? "Videos" : "Shorts"} breakdown`}
          onClick={() => onAskAboutSurface(platform === "videos" ? "YouTube - Videos" : "YouTube - Shorts", platform === "videos" ? "Videos" : "Shorts", platform === "videos" ? score : shortsScore)}
        >
          <IrisSparkIcon size={16} />
        </button>
      </section>
        </>
      )}

      <section className="pulse-v2-card pulse-v2-milestones pulse-v2-iris-jump-host">
        <div className="pulse-v2-milestone-head">
          <div><h2>Your Milestone Path</h2><p>From connected to your first brand open. Change your journey in Settings.</p></div>
          <div><span>Progress = readiness / 10</span><strong>{milestonePct}%</strong></div>
        </div>
        <div className="pulse-v2-milestone-progress"><span style={{ width: `${milestonePct}%` }} /></div>
        <div className="pulse-v2-milestone-list">
          <div className={oauthConnected ? "" : "pending"}><span><LinkRoundedIcon fontSize="inherit" /></span><div><strong>First channel connected</strong><small>{oauthConnected ? "YouTube connected - read-only" : "Connect YouTube analytics"}</small></div></div>
          <div className={scoreReady ? "" : "active"}><span><BarChartRoundedIcon fontSize="inherit" /></span><div><strong>First readiness score</strong><small>{scoreReady ? `YouTube long-form scored ${score.toFixed(1)} - ${scoreConfidence(metrics).toLowerCase()} confidence` : `${samples}/${target} snapshots collected`}</small></div></div>
          <div className="active"><span>$</span><div><strong>{preferences.creator_segment === "established" ? "Develop your partnership offer" : "Build toward monetization"}</strong><small>{preferences.creator_segment === "established" ? "Use your media kit and measured audience evidence to define your offer." : "Explore your eligibility in YouTube Studio. Subscriber count alone does not establish eligibility."}</small></div><em>In progress</em></div>
          <div className={scoreReady ? "" : "pending"}><span><BookmarkBorderRoundedIcon fontSize="inherit" /></span><div><strong>First sponsor-ready kit</strong><small>{scoreReady ? "YouTube long-form kit, brand-ready to share" : "Unlocks with a trusted score"}</small></div></div>
          <div className="pending"><span><MailOutlineRoundedIcon fontSize="inherit" /></span><div><strong>First tracked brand open</strong><small>A brand opens your kit - the first money signal. Private sponsor links and per-viewer tracking are planned after V1.</small></div></div>
        </div>
        <button
          type="button"
          className="pulse-v2-score-card-iris-jump"
          aria-label="Ask IRIS about your milestone path"
          onClick={() => onAskAboutSurface("Your Milestone Path", "your milestone path", milestonePct / 10)}
        >
          <IrisSparkIcon size={16} />
        </button>
      </section>
    </div>
  );
};

const DEFAULT_MEDIA_KIT_TAGS = ["Creator Systems", "Productivity", "SaaS Reviews"];

const MAX_MEDIA_KIT_SOURCE_BYTES = 5 * 1024 * 1024;
// Production traffic passes through Nginx, whose current request ceiling is 1 MB. Keep enough
// room for multipart boundaries and channel_id while retaining more than enough resolution for
// the 1200x360 Media Kit hero. Without this, Nginx returns a headerless 413 that browsers report
// misleadingly as a CORS failure.
const MEDIA_KIT_UPLOAD_TARGET_BYTES = 800 * 1024;

const prepareMediaKitAsset = async (kind: "cover" | "avatar", file: File): Promise<File> => {
  if (file.size > MAX_MEDIA_KIT_SOURCE_BYTES) throw new Error("media-kit-asset-too-large");
  if (!/^image\/(?:jpeg|png|webp)$/i.test(file.type)) throw new Error("media-kit-asset-type");
  if (file.size <= MEDIA_KIT_UPLOAD_TARGET_BYTES) return file;

  const sourceUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const next = new Image();
      next.onload = () => resolve(next);
      next.onerror = () => reject(new Error("media-kit-asset-decode"));
      next.src = sourceUrl;
    });
    const targetWidth = kind === "cover" ? 1200 : 800;
    const targetHeight = kind === "cover" ? 360 : 800;
    const targetRatio = targetWidth / targetHeight;
    const sourceRatio = image.naturalWidth / image.naturalHeight;
    let sx = 0; let sy = 0; let sw = image.naturalWidth; let sh = image.naturalHeight;
    if (sourceRatio > targetRatio) {
      sw = image.naturalHeight * targetRatio;
      sx = (image.naturalWidth - sw) / 2;
    } else {
      sh = image.naturalWidth / targetRatio;
      sy = (image.naturalHeight - sh) / 2;
    }

    const canvas = document.createElement("canvas");
    canvas.width = targetWidth; canvas.height = targetHeight;
    const context = canvas.getContext("2d");
    if (!context) throw new Error("media-kit-asset-canvas");
    context.fillStyle = "#12091f";
    context.fillRect(0, 0, targetWidth, targetHeight);
    context.drawImage(image, sx, sy, sw, sh, 0, 0, targetWidth, targetHeight);

    const encode = (quality: number) => new Promise<Blob>((resolve, reject) => {
      canvas.toBlob((blob) => blob ? resolve(blob) : reject(new Error("media-kit-asset-encode")), "image/jpeg", quality);
    });
    let optimized = await encode(0.88);
    for (const quality of [0.78, 0.68, 0.58, 0.48]) {
      if (optimized.size <= MEDIA_KIT_UPLOAD_TARGET_BYTES) break;
      optimized = await encode(quality);
    }
    if (optimized.size > MEDIA_KIT_UPLOAD_TARGET_BYTES) throw new Error("media-kit-asset-compression");
    const baseName = file.name.replace(/\.[^.]+$/, "") || kind;
    return new File([optimized], `${baseName}.jpg`, { type: "image/jpeg", lastModified: Date.now() });
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

const MediaKitEditDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  channelName: string;
  email: string;
  bio: string;
  tags: string[];
  heroUrl: string;
  avatarUrl: string;
  onSave: (value: { channelName: string; bio: string; email: string; tags: string[]; heroUrl: string; avatarUrl: string }) => Promise<void>;
  onSync: () => Promise<DesignerPreview | null>;
  onUpload: (kind: "cover" | "avatar", file: File) => Promise<string>;
}> = ({ open, onClose, channelName, email, bio, tags, heroUrl, avatarUrl, onSave, onSync, onUpload }) => {
  const [draftName, setDraftName] = useState(channelName);
  const [draftEmail, setDraftEmail] = useState(email);
  const [draftBio, setDraftBio] = useState(bio);
  const [draftTags, setDraftTags] = useState(tags);
  const [draftHero, setDraftHero] = useState(heroUrl);
  const [draftAvatar, setDraftAvatar] = useState(avatarUrl);
  const [tagDraft, setTagDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [message, setMessage] = useState("");
  const [syncError, setSyncError] = useState(false);
  useEffect(() => {
    if (!open) return;
    setDraftName(channelName); setDraftEmail(email); setDraftBio(bio); setDraftTags(tags);
    setDraftHero(heroUrl); setDraftAvatar(avatarUrl); setTagDraft(""); setMessage(""); setSyncError(false);
    // Initialize a fresh draft only when the dialog opens. Provider sync
    // updates the parent props while the dialog remains open; reacting to
    // those updates here would immediately erase the sync result message.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const close = (event: KeyboardEvent) => { if (event.key === "Escape") onClose(); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [open, onClose]);
  if (!open) return null;
  const addTag = () => {
    const next = tagDraft.trim().replace(/,+$/, "");
    if (next && !draftTags.some((tag) => tag.toLowerCase() === next.toLowerCase())) setDraftTags((current) => [...current, next]);
    setTagDraft("");
  };
  const save = async () => {
    setBusy(true); setMessage(""); setSyncError(false);
    try {
      await onSave({ channelName: draftName, bio: draftBio, email: draftEmail, tags: draftTags, heroUrl: draftHero, avatarUrl: draftAvatar });
      onClose();
    } catch { setMessage("Could not save changes. Please retry."); } finally { setBusy(false); }
  };
  const sync = async () => {
    setSyncing(true); setMessage(""); setSyncError(false);
    try {
      const channel = await onSync();
      if (!channel) throw new Error("No connected channel returned.");
      const nextName = String(channel.title || draftName).trim();
      const nextBio = String(channel.description || "");
      const nextAvatar = String(channel.thumbnail_url || "");
      const nextHero = String(channel.cover_url || draftHero);
      setDraftName(nextName); setDraftBio(nextBio); setDraftAvatar(nextAvatar); setDraftHero(nextHero);
      await onSave({ channelName: nextName, bio: nextBio, email: draftEmail, tags: draftTags, heroUrl: nextHero, avatarUrl: nextAvatar });
      setMessage("Synced from the connected channel. Channel data replaced the previous values.");
    } catch (error: any) {
      setSyncError(true);
      setMessage(error?.response?.status === 409
        ? "The channel provider could not refresh right now. Your existing details were kept."
        : "Could not sync the connected channel. Your existing details were kept.");
    } finally { setSyncing(false); }
  };
  const upload = async (kind: "cover" | "avatar", file?: File) => {
    if (!file) return;
    setUploading(true); setMessage(""); setSyncError(false);
    try {
      const url = await onUpload(kind, file);
      if (kind === "cover") setDraftHero(url); else setDraftAvatar(url);
    } catch {
      setSyncError(true); setMessage("Could not upload that image. Use a PNG, JPG, or WebP file under 5MB.");
    } finally { setUploading(false); }
  };
  return <div className="pulse-v2-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="pulse-v2-edit-dialog" role="dialog" aria-modal="true" aria-labelledby="media-kit-edit-title">
      <header><div><h2 id="media-kit-edit-title">Edit Bio</h2><p>Customise the identity shown on your media kit brand header.</p></div><button type="button" aria-label="Close Edit Bio" onClick={onClose}>×</button></header>
      <div className="pulse-v2-edit-body">
        <div className="pulse-v2-edit-intro"><p>These details appear only on your media kit.</p><button type="button" onClick={sync} disabled={syncing}><SyncRoundedIcon fontSize="small" /> {syncing ? "Syncing channel…" : "Sync from channel"}</button></div>
        <label className="pulse-v2-edit-field"><span>Cover Image</span><div className="pulse-v2-cover-upload">{draftHero ? <img src={draftHero} alt="Cover preview" /> : <><CloudUploadOutlinedIcon /><strong>{uploading ? "Optimising &amp; uploading…" : "Drag &amp; drop or browse"}</strong><small>Recommended: 1200×360px. Large images are optimised automatically.</small></>}<input aria-label="Upload cover image" type="file" accept="image/png,image/jpeg,image/webp" disabled={uploading} onChange={(event) => void upload("cover", event.target.files?.[0])} /></div>{draftHero && <button className="pulse-v2-text-button" type="button" onClick={() => setDraftHero("")}>Remove cover</button>}</label>
        <label className="pulse-v2-edit-field"><span>Channel Icon</span><div className="pulse-v2-avatar-upload"><span>{draftAvatar ? <img src={draftAvatar} alt="Channel icon preview" /> : initials(draftName)}</span><div><strong>{uploading ? "Uploading…" : "Upload New Picture"}</strong><small>Square image, up to 5MB (PNG, JPG, WebP)</small><input aria-label="Upload channel icon" type="file" accept="image/png,image/jpeg,image/webp" disabled={uploading} onChange={(event) => void upload("avatar", event.target.files?.[0])} /></div></div></label>
        <label className="pulse-v2-edit-field"><span>Channel Name</span><input value={draftName} readOnly aria-readonly="true" /><small>Read from the active connected channel. Use Sync to refresh it.</small></label>
        <label className="pulse-v2-edit-field"><span>Email</span><input type="email" value={draftEmail} onChange={(event) => setDraftEmail(event.target.value)} /></label>
        <label className="pulse-v2-edit-field"><span>Tags</span><div className="pulse-v2-tag-editor"><div>{draftTags.map((tag) => <span key={tag}>{tag}<button type="button" aria-label={`Remove ${tag}`} onClick={() => setDraftTags((current) => current.filter((value) => value !== tag))}>×</button></span>)}</div><input value={tagDraft} placeholder="Type and press enter to add tag…" onChange={(event) => setTagDraft(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter" || event.key === ",") { event.preventDefault(); addTag(); } }} /></div></label>
        <label className="pulse-v2-edit-field"><span>Bio</span><textarea rows={4} value={draftBio} onChange={(event) => setDraftBio(event.target.value)} /></label>
        {message && <p className={`pulse-v2-edit-message ${syncError ? "error" : ""}`} role="status">{message}</p>}
      </div>
      <footer><button type="button" onClick={onClose}>Cancel</button><button className="primary" type="button" onClick={save} disabled={busy || syncing || uploading}>{busy ? "Saving…" : "Save Changes"}</button></footer>
    </section>
  </div>;
};

export const PulseMediaKitDashboard: React.FC<{
  preferences: PulsePreferences;
  onPreferencesSave: (value: PulsePreferences) => Promise<void>;
  onChannelSync: () => Promise<DesignerPreview | null>;
  metrics: DesignerMediaMetrics;
  account: Account;
  preview: DesignerPreview | null;
  accountEmail?: string | null;
  pulseHandle?: string | null;
  recommendation: DesignerRecommendation | null;
  onShare: () => void;
  onExport: () => void | Promise<void>;
  actionMessage: string;
}> = ({ metrics, account, preview, accountEmail, pulseHandle, recommendation, onShare, onExport, actionMessage, preferences, onPreferencesSave, onChannelSync }) => {
  const [tab, setTab] = useState("kit");
  const [editorOpen, setEditorOpen] = useState(false);
  const visibilityKey = `pulse_media_kit_visibility:${preview?.channel_id || "primary"}`;
  const identityKey = `pulse_media_kit_identity:${preview?.channel_id || "primary"}`;
  const [localIdentity, setLocalIdentity] = useState<{ email?: string; tags?: string[] }>(() => {
    try { return JSON.parse(window.localStorage.getItem(identityKey) || "{}"); } catch { return {}; }
  });
  // Maps each Customise toggle to the server-persisted preference field Jim's
  // public share endpoint reads (visibility must live server-side, not just
  // localStorage, so a stranger viewing the public link sees the real state).
  const VISIBILITY_PREF_KEY: Record<string, keyof PulsePreferences> = {
    profile: "visible_profile",
    bioIntro: "visible_bio_intro",
    subscribers: "visible_metrics_subscribers",
    avgViews: "visible_metrics_avg_views",
    engagement: "visible_metrics_engagement",
    demographics: "visible_metrics_demographics",
    partnershipFit: "visible_iris_partnership_fit",
    partnershipStatement: "visible_iris_partnership_fit",
  };
  const defaultVisibility = { profile: true, bioIntro: true, subscribers: true, avgViews: true, engagement: true, demographics: true, partnershipFit: true, partnershipStatement: true };
  const visibilityFromPreferences = (prefs: PulsePreferences): Record<string, boolean> | null => {
    const hasSaved = Object.values(VISIBILITY_PREF_KEY).some((key) => prefs[key] !== undefined);
    if (!hasSaved) return null;
    return Object.fromEntries(Object.entries(VISIBILITY_PREF_KEY).map(([localKey, prefKey]) => [localKey, prefs[prefKey] !== false]));
  };
  const [visibility, setVisibility] = useState<Record<string, boolean>>(() => {
    const fromPrefs = visibilityFromPreferences(preferences);
    if (fromPrefs) return fromPrefs;
    try { return { ...defaultVisibility, ...JSON.parse(window.localStorage.getItem(visibilityKey) || "null") }; }
    catch { return defaultVisibility; }
  });
  const [access, setAccess] = useState<MediaKitAccess | null>(null);
  const [accessLoading, setAccessLoading] = useState(false);
  const [accessSaving, setAccessSaving] = useState(false);
  const [accessError, setAccessError] = useState("");
  const [copyLabel, setCopyLabel] = useState("Copy link");
  const [passcodeCopyLabel, setPasscodeCopyLabel] = useState("Copy code");
  const [emailDraft, setEmailDraft] = useState("");
  const visibilityKeyRef = useRef(visibilityKey);
  useEffect(() => {
    if (visibilityKeyRef.current !== visibilityKey) {
      visibilityKeyRef.current = visibilityKey;
      const fromPrefs = visibilityFromPreferences(preferences);
      if (fromPrefs) { setVisibility(fromPrefs); return; }
      try { setVisibility({ ...defaultVisibility, ...JSON.parse(window.localStorage.getItem(visibilityKey) || "null") }); }
      catch { /* retain the current defaults */ }
      return;
    }
    try { window.localStorage.setItem(visibilityKey, JSON.stringify(visibility)); } catch { /* local preference only */ }
  }, [visibility, visibilityKey]);
  useEffect(() => {
    try { setLocalIdentity(JSON.parse(window.localStorage.getItem(identityKey) || "{}")); } catch { setLocalIdentity({}); }
  }, [identityKey]);
  useEffect(() => {
    if (tab !== "access") return;
    let cancelled = false;
    setAccessLoading(true); setAccessError("");
    pulseApi.get<MediaKitAccess>(`${API_BASE}/api/media-kit/access`)
      .then(({ data }) => { if (!cancelled) setAccess(data); })
      .catch(() => { if (!cancelled) setAccessError("Access settings are temporarily unavailable."); })
      .finally(() => { if (!cancelled) setAccessLoading(false); });
    return () => { cancelled = true; };
  }, [tab, preview?.channel_id]);
  const accountName = creatorName(account, preview);
  // The media-kit identity is always the connected channel, never the account profile.
  // Multi-channel selection follows the app's active channel; bootstrap selects the
  // backend-designated active channel, falling back to the first connected channel.
  const name = String(preview?.title || accountName || "Jason Nellis").trim() || "Jason Nellis";
  const email = localIdentity.email || preferences.media_kit_email || accountEmail || "jason@creatorstudio.io";
  const tags = localIdentity.tags?.length ? localIdentity.tags : preferences.media_kit_tags?.length ? preferences.media_kit_tags : DEFAULT_MEDIA_KIT_TAGS;
  const bio = preferences.bio || preview?.description || "";
  // The backend sends one row per YouTube Analytics age_gender dimension key
  // ("age25-34.female", "age25-34.male", etc.) - these are genuinely
  // distinct measured buckets (Jim confirmed against the DB), never to be
  // collapsed or summed. The earlier bug was the display label dropping
  // the gender suffix, so two different buckets both rendered as plain
  // "35-44" - not a data problem. Sort into a sensible order (age bucket,
  // then gender) before slicing so the visible top-N is a coherent prefix
  // instead of whatever order the API happens to return.
  const GENDER_SORT_ORDER: Record<string, number> = { male: 0, female: 1, other: 2 };
  const ages = [...metrics.audience_age_distribution]
    .sort((a, b) => {
      const pa = parseAgeGenderKey(a.label);
      const pb = parseAgeGenderKey(b.label);
      const ageDiff = AGE_BUCKET_ORDER.indexOf(pa.ageLabel) - AGE_BUCKET_ORDER.indexOf(pb.ageLabel);
      if (ageDiff !== 0) return ageDiff;
      return (GENDER_SORT_ORDER[pa.gender || "other"] ?? 3) - (GENDER_SORT_ORDER[pb.gender || "other"] ?? 3);
    })
    .slice(0, 5);
  const geos = metrics.audience_geo_distribution.slice(0, 5);
  const toggleGroups = [
    { group: "Profile", items: [["profile", "Profile"], ["bioIntro", "Bio & Intro"]] },
    { group: "Metrics shared", items: [["subscribers", "Subscribers"], ["avgViews", "Avg. views / video"], ["engagement", "Engagement"], ["demographics", "Audience demographics"], ["partnershipFit", "IRIS Partnership Fit"]] },
    { group: "IRIS", items: [["partnershipStatement", "Partnership fit statement"]] },
  ];
  const toggleVisibility = (key: string) => {
    const next = !visibility[key];
    setVisibility((current) => ({ ...current, [key]: next }));
    const prefKey = VISIBILITY_PREF_KEY[key];
    if (prefKey) void onPreferencesSave({ ...preferences, [prefKey]: next });
  };
  const saveIdentity = async (value: { channelName: string; bio: string; email: string; tags: string[]; heroUrl: string; avatarUrl: string }) => {
    const nextLocal = { email: value.email, tags: value.tags };
    setLocalIdentity(nextLocal);
    try { window.localStorage.setItem(identityKey, JSON.stringify(nextLocal)); } catch { /* local media-kit identity cache */ }
    await onPreferencesSave({
      ...preferences,
      display_name: value.channelName,
      bio: value.bio,
      media_kit_email: value.email,
      media_kit_tags: value.tags,
      hero_url: value.heroUrl,
      headshot_url: value.avatarUrl,
    });
  };
  const uploadIdentityAsset = async (kind: "cover" | "avatar", file: File) => {
    if (!preview?.channel_id) throw new Error("No connected channel selected");
    const preparedFile = await prepareMediaKitAsset(kind, file);
    const form = new FormData();
    form.append("channel_id", preview.channel_id);
    form.append("file", preparedFile);
    const { data } = await pulseApi.post<{ url: string }>(`${API_BASE}/api/media-kit/asset/${kind}`, form);
    return /^https?:/i.test(data.url) ? data.url : `${API_BASE.replace(/\/$/, "")}/${data.url.replace(/^\//, "")}`;
  };
  const saveAccess = async (patch: Partial<MediaKitAccess>) => {
    if (!access || accessSaving) return;
    const previous = access;
    const next = { ...access, ...patch };
    setAccess(next);
    setAccessSaving(true); setAccessError("");
    try {
      const { data } = await pulseApi.put<MediaKitAccess>(`${API_BASE}/api/media-kit/access`, {
        channel_id: preview?.channel_id || undefined,
        require_passcode: next.require_passcode,
        regenerate_passcode: patch.regenerate_passcode === true,
        viewer_watermark: next.viewer_watermark,
        expiration_enabled: next.expiration_enabled,
        notify_on_view: next.notify_on_view,
        approved_emails: next.approved_emails,
      });
      setAccess(data);
    } catch {
      setAccess(previous);
      setAccessError("Could not save access settings. Please retry.");
    } finally { setAccessSaving(false); }
  };
  const addApprovedEmail = async () => {
    const value = emailDraft.trim().toLowerCase();
    if (!access || !/^\S+@\S+\.\S+$/.test(value) || access.approved_emails.includes(value)) return;
    await saveAccess({ approved_emails: [...access.approved_emails, value] });
    setEmailDraft("");
  };
  // The public share page lives on this frontend at /mk/:handle/share/:slug -
  // rebuild the link from whatever slug the backend returns rather than
  // trusting its domain/path, so this never points at another app's origin.
  // Uses the Pulse profile handle (the identity a sponsor should see), never
  // the connected YouTube channel's handle.
  const shareHandle = pulseHandle ? (pulseHandle.startsWith("@") ? pulseHandle : `@${pulseHandle}`) : creatorHandle(account, preview);
  const publicShareUrl = access?.share_url
    ? `${window.location.origin}/mk/${shareHandle}/share/${access.share_url.split("/").filter(Boolean).pop()}`
    : "";
  const copyAccessLink = async () => {
    if (!publicShareUrl) return;
    try { await navigator.clipboard.writeText(publicShareUrl); setCopyLabel("Copied"); window.setTimeout(() => setCopyLabel("Copy link"), 1800); }
    catch { setCopyLabel("Copy failed"); }
  };
  const copyPasscode = async () => {
    if (!access?.passcode) return;
    try {
      await navigator.clipboard.writeText(access.passcode);
      setPasscodeCopyLabel("Copied");
      window.setTimeout(() => setPasscodeCopyLabel("Copy code"), 1800);
    } catch { setPasscodeCopyLabel("Copy failed"); }
  };
  return (
    <div className="pulse-v2-page pulse-v2-media">
      <header className="pulse-v2-page-head pulse-v2-media-head"><div><h1>Creator Media Kit</h1><p>One link, one access log · a section per connected platform, updated by IRIS</p></div></header>
      <section className="pulse-v2-media-hero">
        <div
          className="pulse-v2-media-cover"
          style={preferences.hero_url ? { backgroundImage: `linear-gradient(0deg, rgba(13, 10, 24, .18), rgba(13, 10, 24, 0)), url(${JSON.stringify(preferences.hero_url)})` } : undefined}
        >
          {preferences.logo_url && <img className="pulse-review-brand-logo" src={preferences.logo_url} alt="Creator logo" />}
        </div>
        <div className="pulse-v2-media-profile-row">
          <div className="pulse-v2-media-identity">
            <Avatar account={{ ...account, creatorName: name }} preview={{ ...preview, thumbnail_url: preferences.headshot_url || preview?.thumbnail_url }} large />
            <h2>{name}</h2>
            <p>{email}</p>
            <div className="pulse-v2-media-tags">{tags.map((tag) => <span key={tag}>{tag}</span>)}</div>
            {visibility.bioIntro && <p className="pulse-v2-media-bio">{bio}</p>}
          </div>
          <div className="pulse-v2-media-actions">
            <button type="button" onClick={() => setEditorOpen(true)}><PersonOutlineRoundedIcon fontSize="small" /> Edit Bio</button>
            <button type="button" onClick={onShare}><ShareRoundedIcon fontSize="small" /> Share</button>
            <button className="primary" type="button" onClick={onExport}><FileDownloadRoundedIcon fontSize="small" /> Export PDF</button>
          </div>
        </div>
      </section>
      <MediaKitEditDialog
        open={editorOpen}
        onClose={() => setEditorOpen(false)}
        channelName={name}
        email={email}
        bio={bio}
        tags={tags}
        heroUrl={preferences.hero_url || ""}
        avatarUrl={preferences.headshot_url || preview?.thumbnail_url || ""}
        onSave={saveIdentity}
        onSync={onChannelSync}
        onUpload={uploadIdentityAsset}
      />
      {actionMessage ? <div className="pulse-v2-action-message">{actionMessage}</div> : null}
      <div className="pulse-v2-media-tabs">
        <button className={tab === "kit" ? "active" : ""} type="button" onClick={() => setTab("kit")}><ReceiptLongOutlinedIcon /> Media Kit</button>
        <button className={tab === "customise" ? "active" : ""} type="button" onClick={() => setTab("customise")}><GridViewRoundedIcon /> Customise</button>
        <button className={tab === "access" ? "active" : ""} type="button" onClick={() => setTab("access")}><CheckRoundedIcon /> Access</button>
      </div>
      {tab === "customise" && <div className="pulse-v2-customise">{toggleGroups.map((group) => <section className="pulse-v2-card" key={group.group}><h3>{group.group}</h3>{group.items.map(([key, label]) => <div className="pulse-v2-customise-row" key={key}><span>{label}</span><button type="button" role="switch" aria-label={`Toggle ${label}`} aria-checked={visibility[key]} className={`pulse-v2-switch ${visibility[key] ? "on" : ""}`} onClick={() => toggleVisibility(key)} /></div>)}</section>)}</div>}
      {tab === "access" && <div className="pulse-v2-access">
        {accessLoading ? <section className="pulse-v2-card pulse-v2-access-loading">Loading access settings…</section> : access ? <>
          <section className="pulse-v2-card pulse-v2-share-link"><h3>Shareable link</h3><div><span>{publicShareUrl}</span><button type="button" onClick={copyAccessLink}><LinkRoundedIcon /> {copyLabel}</button></div><p>Anyone with this link can view — no login required.</p></section>
          <section className="pulse-v2-card pulse-v2-access-settings"><h3>Access settings</h3>{[
            ["require_passcode", "Require passcode", "Viewers must enter a code you set"],
            ["viewer_watermark", "Viewer watermark", "Stamps your name, email, and view time onto every screen a sponsor sees."],
            ["expiration_enabled", "Set expiration", access.expires_at ? `Expires ${new Date(access.expires_at).toLocaleDateString()}` : "Never expires"],
            ["notify_on_view", "Notify me when viewed", "Get a ping the moment a sponsor opens this"],
          ].map(([key, label, help]) => <React.Fragment key={key}>
            <div className="pulse-v2-access-setting"><div><strong>{label}</strong><small>{help}</small></div><button type="button" role="switch" aria-label={label} aria-checked={Boolean(access[key as keyof MediaKitAccess])} className={`pulse-v2-switch ${access[key as keyof MediaKitAccess] ? "on" : ""}`} disabled={accessSaving} onClick={() => void saveAccess({ [key]: !access[key as keyof MediaKitAccess] })} /></div>
            {key === "require_passcode" && access.require_passcode ? <div className="pulse-v2-passcode" aria-live="polite">
              <div><small>Your viewer passcode</small><strong>{access.passcode || "Generating…"}</strong><p>Share this code separately with approved viewers.</p></div>
              <div>
                <button type="button" disabled={!access.passcode || accessSaving} onClick={() => void copyPasscode()}><LinkRoundedIcon fontSize="small" /> {passcodeCopyLabel}</button>
                <button type="button" disabled={accessSaving} onClick={() => void saveAccess({ regenerate_passcode: true })}><SyncRoundedIcon fontSize="small" /> {accessSaving ? "Updating…" : "Regenerate"}</button>
              </div>
            </div> : null}
          </React.Fragment>)}</section>
          <section className="pulse-v2-card pulse-v2-approved">
            <h3>Approved emails</h3>
            <div className="pulse-v2-approved-add">
              <input
                type="email"
                value={emailDraft}
                placeholder="name@brand.com"
                onChange={(event) => setEmailDraft(event.target.value)}
                onKeyDown={(event) => { if (event.key === "Enter") void addApprovedEmail(); }}
              />
              <button type="button" onClick={() => void addApprovedEmail()}><AddRoundedIcon fontSize="small" /> Add</button>
            </div>
            {access.approved_emails.length ? <div className="pulse-v2-approved-list">{access.approved_emails.map((approvedEmail) => <span className="pulse-v2-approved-chip" key={approvedEmail}>{approvedEmail}<button type="button" aria-label={`Remove ${approvedEmail}`} onClick={() => void saveAccess({ approved_emails: access.approved_emails.filter((item) => item !== approvedEmail) })}><CloseRoundedIcon fontSize="inherit" /></button></span>)}</div> : null}
            <p>Only these addresses can open the kit. Each verifies with a one-time code — no account needed.</p>
          </section>
          <section className="pulse-v2-card pulse-v2-access-activity"><h3>Recent activity</h3>{access.recent_activity.length ? access.recent_activity.map((item) => <div key={item.id}><VisibilityOutlinedIcon fontSize="small" /> <span>{item.viewer_name} viewed · {formatAccessTime(item.viewed_at)}</span></div>) : <p>No sponsor views yet.</p>}</section>
        </> : <section className="pulse-v2-card pulse-v2-access-loading">{accessError || "Access settings are unavailable."}</section>}
        {accessError && access ? <p className="pulse-v2-access-error" role="status">{accessError}</p> : null}
      </div>}
      {tab === "kit" && <><section className="pulse-v2-card pulse-v2-channel-evidence">
        <h2><span>YouTube</span> - Long-form</h2>
        <div>
          {visibility.subscribers && <article><span>Subscribers</span><strong>{metrics.followers || typeof preview?.subscriber_count === "number" ? compactNumber(metrics.followers || preview?.subscriber_count) : "--"}</strong><small>Current public total</small></article>}
          {visibility.avgViews && <article><span>Total views</span><strong>{typeof preview?.view_count === "number" ? compactNumber(preview.view_count) : "--"}</strong><small>Current public total</small></article>}
          <article><span>Avg. monthly reach</span><strong>{metrics.avg_monthly_reach ? compactNumber(metrics.avg_monthly_reach) : "--"}</strong><small>Connected analytics</small></article>
          {visibility.engagement && <article><span>Engagement rate</span><strong>{metrics.engagement_rate ? `${metrics.engagement_rate.toFixed(1)}%` : "--"}</strong><small>{metrics.engagement_rate ? "Measured from connected data" : "Data pending"}</small></article>}
        </div>
      </section>
      {visibility.demographics && <div className="pulse-v2-demographic-grid">
        <section className="pulse-v2-card"><h2>Age Distribution</h2>{ages.length ? ages.map((row) => {
          const { ageLabel, gender, genderLabel } = parseAgeGenderKey(row.label);
          return (
            <div className="pulse-v2-demographic-row" key={row.label}>
              <span className="pulse-v2-demographic-label">
                {gender === "male" ? <MaleRoundedIcon fontSize="inherit" aria-hidden="true" /> : null}
                {gender === "female" ? <FemaleRoundedIcon fontSize="inherit" aria-hidden="true" /> : null}
                {ageLabel}
                {genderLabel ? ` · ${genderLabel}` : ""}
              </span>
              <i><b style={{ width: `${parsePercent(row.value)}%` }} /></i>
              <strong>{row.value}</strong>
            </div>
          );
        }) : <EmptyMetric label="Age data pending" />}</section>
        <section className="pulse-v2-card"><h2>Geographic Reach</h2>{geos.length ? geos.map((row) => {
          const flag = countryFlagEmoji(row.label);
          const name = flag ? countryName(row.label) : row.label;
          return (
            <div className="pulse-v2-demographic-row" key={row.label}>
              <span className="pulse-v2-demographic-label">
                {flag ? <span aria-hidden="true">{flag}</span> : null}
                {name}
              </span>
              <i><b style={{ width: `${parsePercent(row.value)}%` }} /></i>
              <strong>{row.value}</strong>
            </div>
          );
        }) : <EmptyMetric label="Geography data pending" />}</section>
      </div>}
      {visibility.partnershipFit && <section className="pulse-v2-partnership"><h2><AutoAwesomeRoundedIcon fontSize="small" /> Partnership Fit</h2>{visibility.partnershipStatement && <p>{recommendation?.summary || "IRIS is gathering enough connected-channel evidence to write a trustworthy partnership fit."} {recommendation?.action || ""}</p>}</section>}
      <section className="pulse-v2-card pulse-v2-top-content"><h2>Top Performing Content</h2><p>{metrics.top_content_subtitle}</p>{metrics.top_content.length ? metrics.top_content.slice(0, 3).map((item) => <article key={item.title}><div><strong>{item.url ? <a href={item.url} target="_blank" rel="noreferrer">{item.title}</a> : item.title}</strong><span>{compactNumber(item.views)} views - {item.engagement_rate.toFixed(1)}% engagement</span></div></article>) : <EmptyMetric label="No qualifying videos in this period" />}</section>
      </>}
    </div>
  );
};

type LibraryTab = "metrics" | "actions";

export const PulseBookmarkDashboard: React.FC<{
  activeTab: LibraryTab;
  items: DesignerSavedItem[];
  memory: DesignerMemory;
  memoryLoading: boolean;
  loading: boolean;
  error: string | null;
  onTabChange: (tab: LibraryTab) => void;
  onAskSuggestion: (prompt: string) => void;
  onAskAboutItem: (item: DesignerSavedItem) => void;
  onTogglePinned: (item: any) => void;
  onDelete: (item: any) => void;
  onClearMemory: () => void;
  onClearSaved: () => void;
}> = ({ activeTab, items, memory, memoryLoading, loading, error, onTabChange, onAskSuggestion, onAskAboutItem, onTogglePinned, onDelete, onClearMemory, onClearSaved }) => {
  const [activeFilter, setActiveFilter] = useState("");
  const filters = ["Audience & ICPs", "Monetization & sponsors", "Format & packaging", "Posting cadence", "Data & scope"];
  const filtered = items.filter((item) =>
    item.type === (activeTab === "metrics" ? "metric" : "action")
    && (!activeFilter || item.category === activeFilter),
  );
  const memoryTopics = Array.isArray(memory.topics) ? memory.topics : [];
  return (
    <div className="pulse-v2-page pulse-v2-bookmarks">
      <header className="pulse-v2-page-head"><div><h1>Bookmark</h1><p>Metrics and actions you've saved from IRIS, filed by topic - each one also feeds IRIS's context for future conversations, and you can jump back into a conversation about any of them.</p></div></header>
      <div className="pulse-v2-firewall">Save a metric to pin it on Insights, or an action to track it as a move - both stay scoped to your connected YouTube channel, and both are things IRIS can reference back to you unprompted.</div>
      <section className="pulse-v2-card pulse-v2-memory-card">
        <div className="pulse-v2-card-topline"><div><span className="pulse-v2-eyebrow">Persistent context</span><h2>What IRIS remembers</h2></div><button type="button" onClick={onClearMemory} disabled={memoryLoading || !Object.keys(memory).length}>Clear memory</button></div>
        <p className="pulse-v2-memory-subcopy">Builds automatically from your conversations - separate from the metrics and actions you save below.</p>
        {memoryLoading ? <p>Loading creator memory...</p> : memoryTopics.length ? (
          <div className="pulse-v2-memory-topics">{memoryTopics.map((row) => <article key={row.topic}><strong>{row.topic}</strong><p>{row.summary}</p></article>)}</div>
        ) : <p>IRIS has not stored cross-session channel context yet. Your next grounded conversation will start building it.</p>}
      </section>
      <div className="pulse-v2-bookmark-tabs"><button className={activeTab === "metrics" ? "active" : ""} onClick={() => onTabChange("metrics")} type="button">Saved Metrics</button><button className={activeTab === "actions" ? "active" : ""} onClick={() => onTabChange("actions")} type="button">Saved Actions</button></div>
      <div className="pulse-v2-filter-row"><TuneRoundedIcon fontSize="small" /><button className={!activeFilter ? "active" : ""} type="button" onClick={() => setActiveFilter("")}>All</button>{filters.map((filter) => <button className={activeFilter === filter ? "active" : ""} type="button" key={filter} onClick={() => setActiveFilter(filter)}>{filter}</button>)}<button className="danger" type="button" onClick={onClearSaved} disabled={!items.length}>Clear saved</button></div>
      {error ? <div className="pulse-v2-action-message error">{error}</div> : null}
      {loading ? <div className="pulse-v2-bookmark-grid"><article className="pulse-v2-card loading" /><article className="pulse-v2-card loading" /></div> : filtered.length ? (
        <div className="pulse-v2-bookmark-grid">{filtered.map((item) => {
          const date = item.created_at ? new Intl.DateTimeFormat("en", { month: "short", day: "numeric" }).format(new Date(item.created_at)) : "just now";
          const isAction = item.type === "action";
          return <article className="pulse-v2-card" key={item.id}><div><em>{item.category || "Data & scope"}</em><span>Saved {date}</span></div><strong className="pulse-v2-saved-value">{item.value || item.title}</strong><h2>{item.title}</h2><div className="pulse-v2-saved-body"><ReactMarkdown remarkPlugins={[remarkGfm]}>{item.body}</ReactMarkdown></div><footer><button className={item.pinned ? "active" : ""} type="button" onClick={() => onTogglePinned(item)}><PushPinOutlinedIcon fontSize="small" /> {item.pinned ? (isAction ? "In this week's moves" : "On Insights") : "Add"}</button><button type="button" onClick={() => onAskAboutItem(item)}><AutoAwesomeRoundedIcon fontSize="small" /> Ask IRIS</button><button type="button" onClick={() => onDelete(item)}><DeleteOutlineRoundedIcon fontSize="small" /> Delete</button></footer></article>;
        })}</div>
      ) : <section className="pulse-v2-card pulse-v2-empty"><BookmarkBorderRoundedIcon /><h2>No {activeTab} saved yet</h2><p>Ask IRIS for a useful {activeTab === "metrics" ? "metric" : "next move"}, then bookmark the response here.</p><button className="pulse-v2-primary" type="button" onClick={() => onAskSuggestion(activeTab === "metrics" ? "What is my average engagement rate?" : "What should I do next week?")}><AutoAwesomeRoundedIcon fontSize="small" /> Ask IRIS</button></section>}
    </div>
  );
};

const ProfileEditor: React.FC<{
  profile: DesignerProfile;
  fallbackName: string;
  saving: boolean;
  error: string | null;
  onSave: (patch: { display_name?: string; bio?: string }) => Promise<void>;
  onCheckHandleAvailable: (value: string) => Promise<boolean>;
  onSaveHandle: (value: string) => Promise<void>;
  avatarUploading: boolean;
  avatarError: string | null;
  onUploadAvatar: (file: File) => Promise<void>;
}> = ({
  profile,
  fallbackName,
  saving,
  error,
  onSave,
  onCheckHandleAvailable,
  onSaveHandle,
  avatarUploading,
  avatarError,
  onUploadAvatar,
}) => {
  const [name, setName] = useState(profile.display_name || fallbackName);
  const [bio, setBio] = useState(profile.bio || "");
  const originalHandle = (profile.handle || "").replace(/^@/, "");
  const [handle, setHandle] = useState(originalHandle);
  const [handleStatus, setHandleStatus] = useState<"idle" | "checking" | "available" | "taken">("idle");
  const [saved, setSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleTimerRef = useRef<number | null>(null);

  const onHandleInput = (value: string) => {
    const clean = value.replace(/^@/, "").toLowerCase().replace(/[^a-z0-9_.-]/g, "");
    setHandle(clean);
    setSaved(false);
    if (handleTimerRef.current) window.clearTimeout(handleTimerRef.current);
    if (!clean || clean === originalHandle) {
      setHandleStatus("idle");
      return;
    }
    setHandleStatus("checking");
    handleTimerRef.current = window.setTimeout(async () => {
      const available = await onCheckHandleAvailable(clean);
      setHandleStatus(available ? "available" : "taken");
    }, 400);
  };

  const handleSaveAll = async () => {
    try {
      if (handle && handle !== originalHandle) {
        if (handleStatus === "taken") return;
        await onSaveHandle(handle);
      }
      await onSave({ display_name: name.trim(), bio });
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2000);
    } catch {
      // surfaced via the `error` prop
    }
  };

  // Account-level Pulse identity only - never falls back to the active
  // channel's thumbnail, so this stays fixed while switching channels.
  const avatarSrc = profile.avatar_url || undefined;
  const displayHandle = profile.handle
    ? profile.handle.startsWith("@") ? profile.handle : `@${profile.handle}`
    : handle
    ? `@${handle}`
    : "@creator";

  return (
    <section className="pulse-v2-card pulse-v2-profile-form">
      <div className="pulse-v2-profile-identity">
        <button
          type="button"
          className="pulse-v2-profile-avatar-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={avatarUploading}
          aria-label="Change avatar"
        >
          <span className="pulse-v2-avatar large">
            {avatarSrc ? <img src={avatarSrc} alt="" referrerPolicy="no-referrer" /> : initials(name || fallbackName)}
          </span>
          <span className="pulse-v2-profile-avatar-overlay">
            <PhotoCameraRoundedIcon fontSize="small" />
          </span>
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp"
          hidden
          onChange={(event) => {
            const file = event.target.files?.[0];
            event.target.value = "";
            if (file) void onUploadAvatar(file);
          }}
        />
        <div>
          <strong>{name || fallbackName}</strong>
          <span>{displayHandle}</span>
        </div>
      </div>
      {avatarUploading ? <p className="pulse-v2-profile-hint">Uploading photo...</p> : null}
      {avatarError ? <div className="pulse-v2-action-message error">{avatarError}</div> : null}
      <label>
        Name
        <input value={name} onChange={(event) => { setName(event.target.value); setSaved(false); }} placeholder={fallbackName} />
      </label>
      <label>
        Handle
        <div className="pulse-v2-handle-input">
          <span>@</span>
          <input value={handle} onChange={(event) => onHandleInput(event.target.value)} placeholder="yourhandle" />
          {handleStatus === "checking" ? <span className="pulse-v2-handle-status checking">Checking...</span> : null}
          {handleStatus === "available" ? <span className="pulse-v2-handle-status available"><CheckCircleRoundedIcon fontSize="small" /> Available</span> : null}
          {handleStatus === "taken" ? <span className="pulse-v2-handle-status taken"><ErrorOutlineRoundedIcon fontSize="small" /> Taken</span> : null}
        </div>
      </label>
      <label>
        Bio
        <textarea value={bio} onChange={(event) => { setBio(event.target.value); setSaved(false); }} rows={4} placeholder="Tell sponsors and collaborators about yourself." />
        <small>Shown on your Media Kit's sponsor-facing summary.</small>
      </label>
      {error ? <div className="pulse-v2-action-message error">{error}</div> : null}
      <button
        type="button"
        className="pulse-v2-primary"
        onClick={handleSaveAll}
        disabled={saving || handleStatus === "taken" || handleStatus === "checking"}
      >
        {saving ? "Saving..." : saved ? "Saved" : "Save changes"}
      </button>
    </section>
  );
};

const AccountSecurityEditor: React.FC<{
  email: string | null;
  authProvider: string | null;
  onLogout: () => void;
  passwordChangeBusy: boolean;
  passwordChangeMessage: string | null;
  passwordChangeError: string | null;
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  deleteAccountBusy: boolean;
  deleteAccountError: string | null;
  onRequestAccountDeletion: (password?: string) => Promise<boolean>;
  twoFactorEnabled: boolean;
  twoFactorBusy: boolean;
  onEnable2fa: () => Promise<{ secret: string; otpauth_url: string } | null>;
  onVerify2fa: (code: string) => Promise<{ ok: true; recovery_codes?: string[] } | null>;
  onDisable2fa: (code: string) => Promise<boolean>;
}> = ({
  email,
  authProvider,
  onLogout,
  passwordChangeBusy,
  passwordChangeMessage,
  passwordChangeError,
  onChangePassword,
  deleteAccountBusy,
  deleteAccountError,
  onRequestAccountDeletion,
  twoFactorEnabled,
  twoFactorBusy,
  onEnable2fa,
  onVerify2fa,
  onDisable2fa,
}) => {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [deletePassword, setDeletePassword] = useState("");
  // auth_provider flips to "both" once a Google-only account sets a
  // password via forgot/reset-password, so only "google" (never "both")
  // means there's no password to confirm deletion with.
  const signedInWithGoogle = authProvider === "google";
  const hasPassword = authProvider !== "google";

  // 2FA enable flow: "closed" -> "setup" (QR + secret + code entry) ->
  // "recovery" (show codes once) -> closed, now enabled. Separate modal/step
  // for disabling, since that only needs a single code prompt.
  const [twoFactorStep, setTwoFactorStep] = useState<"closed" | "setup" | "recovery">("closed");
  const [twoFactorSecret, setTwoFactorSecret] = useState("");
  const [twoFactorQrDataUrl, setTwoFactorQrDataUrl] = useState<string | null>(null);
  const [twoFactorSetupCode, setTwoFactorSetupCode] = useState("");
  const [twoFactorSetupError, setTwoFactorSetupError] = useState<string | null>(null);
  const [recoveryCodes, setRecoveryCodes] = useState<string[]>([]);
  const [recoveryCodesCopied, setRecoveryCodesCopied] = useState(false);
  const [disableModalOpen, setDisableModalOpen] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [disableError, setDisableError] = useState<string | null>(null);

  const openTwoFactorSetup = async () => {
    const result = await onEnable2fa();
    if (!result) return;
    setTwoFactorSecret(result.secret);
    setTwoFactorSetupCode("");
    setTwoFactorSetupError(null);
    setTwoFactorQrDataUrl(null);
    setTwoFactorStep("setup");
    try {
      const qrCodeModule = await import("qrcode");
      const dataUrl = await qrCodeModule.default.toDataURL(result.otpauth_url, { margin: 1, width: 220 });
      setTwoFactorQrDataUrl(dataUrl);
    } catch {
      // QR render failed (e.g. offline chunk load) - the manual secret below
      // is always shown too, so setup can still proceed without it.
    }
  };

  const closeTwoFactorModal = () => {
    setTwoFactorStep("closed");
    setTwoFactorSecret("");
    setTwoFactorQrDataUrl(null);
    setTwoFactorSetupCode("");
    setTwoFactorSetupError(null);
    setRecoveryCodes([]);
    setRecoveryCodesCopied(false);
  };

  const handleSubmitTwoFactorSetup = async () => {
    setTwoFactorSetupError(null);
    if (twoFactorSetupCode.trim().length !== 6) {
      setTwoFactorSetupError("Enter the 6-digit code from your authenticator app.");
      return;
    }
    const result = await onVerify2fa(twoFactorSetupCode.trim());
    if (!result) {
      setTwoFactorSetupError("That code didn't work. Check your authenticator app and try again.");
      return;
    }
    if (result.recovery_codes?.length) {
      setRecoveryCodes(result.recovery_codes);
      setTwoFactorStep("recovery");
    } else {
      closeTwoFactorModal();
    }
  };

  const recoveryCodesText = recoveryCodes.join("\n");

  const handleCopyRecoveryCodes = async () => {
    try {
      await navigator.clipboard.writeText(recoveryCodesText);
      setRecoveryCodesCopied(true);
    } catch {
      // Clipboard access can fail (permissions, insecure context) - the
      // codes are still on screen and downloadable, so this is non-fatal.
    }
  };

  const handleDownloadRecoveryCodes = () => {
    const blob = new Blob([recoveryCodesText + "\n"], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "pulse-recovery-codes.txt";
    link.click();
    URL.revokeObjectURL(url);
  };

  const openDisableModal = () => {
    setDisableCode("");
    setDisableError(null);
    setDisableModalOpen(true);
  };

  const handleSubmitDisable = async () => {
    setDisableError(null);
    if (!disableCode.trim()) {
      setDisableError("Enter a code from your authenticator app, or a recovery code.");
      return;
    }
    const ok = await onDisable2fa(disableCode.trim());
    if (ok) {
      setDisableModalOpen(false);
    } else {
      setDisableError("That code didn't work. Check your authenticator app and try again.");
    }
  };

  const handleConfirmDelete = async () => {
    const ok = await onRequestAccountDeletion(hasPassword ? deletePassword : undefined);
    if (ok) setConfirmOpen(false);
  };

  const openPasswordModal = () => {
    setCurrentPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setFormError(null);
    setPasswordModalOpen(true);
  };

  const handleSubmitPasswordChange = async () => {
    setFormError(null);
    if (newPassword.length < 8) {
      setFormError("New password must be at least 8 characters.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setFormError("New password and confirmation don't match.");
      return;
    }
    const ok = await onChangePassword(currentPassword, newPassword);
    if (ok) setPasswordModalOpen(false);
  };

  return (
    <>
      <section className="pulse-v2-card pulse-v2-account-security">
        <label>
          Email
          <div className="pulse-v2-account-email-row">
            <input value={email || ""} placeholder="you@example.com" disabled readOnly />
          </div>
          <small>{signedInWithGoogle ? "Signed in with Google - managed by your Google account." : "Contact support to change your account email."}</small>
        </label>

        <div className="pulse-v2-account-row">
          <span className="pulse-v2-account-row-icon">
            <VpnKeyOutlinedIcon fontSize="small" />
          </span>
          <div>
            <strong>Change password</strong>
            <small>{signedInWithGoogle ? "Only available once your account has a password set" : "Update the password you use to sign in"}</small>
          </div>
          <button type="button" className="pulse-v2-outline-action" onClick={openPasswordModal} disabled={passwordChangeBusy}>
            Change
          </button>
        </div>
        {passwordChangeMessage ? <div className="pulse-v2-action-message">{passwordChangeMessage}</div> : null}
        {passwordChangeError ? <div className="pulse-v2-action-message error">{passwordChangeError}</div> : null}

        <div className="pulse-v2-account-row">
          <span className="pulse-v2-account-row-icon">
            <SecurityOutlinedIcon fontSize="small" />
          </span>
          <div>
            <strong>Two-factor authentication</strong>
            <small>
              {signedInWithGoogle
                ? "Managed via your Google account"
                : twoFactorEnabled
                ? "Enabled - your authenticator app is required at sign-in"
                : "Add an extra layer of security to your account"}
            </small>
          </div>
          {signedInWithGoogle ? (
            <span className="pulse-v2-switch disabled" aria-disabled="true" />
          ) : (
            <button
              type="button"
              role="switch"
              aria-checked={twoFactorEnabled}
              aria-label="Two-factor authentication"
              className={`pulse-v2-switch ${twoFactorEnabled ? "on" : ""}`}
              onClick={() => (twoFactorEnabled ? openDisableModal() : openTwoFactorSetup())}
              disabled={twoFactorBusy}
            />
          )}
        </div>
      </section>

      <span className="pulse-v2-eyebrow pulse-v2-danger-zone-label">Danger zone</span>
      <section className="pulse-v2-card pulse-v2-account-security pulse-v2-danger-zone">
        <button type="button" className="pulse-v2-account-row as-button" onClick={onLogout}>
          <span className="pulse-v2-account-row-icon">
            <LogoutRoundedIcon fontSize="small" />
          </span>
          <div>
            <strong>Log Out</strong>
          </div>
        </button>

        <button
          type="button"
          className="pulse-v2-account-row as-button danger"
          onClick={() => {
            setDeletePassword("");
            setConfirmOpen(true);
          }}
          disabled={deleteAccountBusy}
        >
          <span className="pulse-v2-account-row-icon">
            <DeleteOutlineRoundedIcon fontSize="small" />
          </span>
          <div>
            <strong>Delete Account</strong>
            <small>Permanently erase your account and data</small>
          </div>
        </button>
        {deleteAccountError ? <div className="pulse-v2-action-message error">{deleteAccountError}</div> : null}
      </section>

      {confirmOpen ? (
        <div className="pulse-v2-confirm-overlay" role="presentation" onClick={() => !deleteAccountBusy && setConfirmOpen(false)}>
          <div className="pulse-v2-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="pulse-delete-account-title" onClick={(event) => event.stopPropagation()}>
            <span className="pulse-v2-confirm-icon">
              <DeleteOutlineRoundedIcon fontSize="small" />
            </span>
            <h3 id="pulse-delete-account-title">Are you sure you want to delete your account?</h3>
            <p>
              This starts a 15-day grace period. Log back in any time within 15 days and your account is
              automatically restored, exactly as it was. If you don't log back in within 15 days, your account and
              all its data are permanently deleted and can't be recovered.
            </p>
            {hasPassword ? (
              <label className="pulse-v2-confirm-field">
                Confirm your password
                <input
                  type="password"
                  value={deletePassword}
                  onChange={(event) => setDeletePassword(event.target.value)}
                  autoComplete="current-password"
                />
              </label>
            ) : null}
            {deleteAccountError ? <div className="pulse-v2-action-message error">{deleteAccountError}</div> : null}
            <div className="pulse-v2-confirm-actions">
              <button type="button" className="pulse-v2-outline-action" onClick={() => setConfirmOpen(false)} disabled={deleteAccountBusy}>
                Cancel
              </button>
              <button
                type="button"
                className="pulse-v2-confirm-danger-btn"
                onClick={handleConfirmDelete}
                disabled={deleteAccountBusy || (hasPassword && !deletePassword)}
              >
                {deleteAccountBusy ? "Deleting..." : "Yes, delete my account"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {passwordModalOpen ? (
        <div className="pulse-v2-confirm-overlay" role="presentation" onClick={() => !passwordChangeBusy && setPasswordModalOpen(false)}>
          <div className="pulse-v2-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="pulse-change-password-title" onClick={(event) => event.stopPropagation()}>
            <span className="pulse-v2-confirm-icon neutral">
              <VpnKeyOutlinedIcon fontSize="small" />
            </span>
            <h3 id="pulse-change-password-title">Change password</h3>
            {signedInWithGoogle ? (
              <p>
                Your account currently signs in with Google and has no password set. This form requires your current
                password, so setting an initial password isn't supported here yet.
              </p>
            ) : (
              <>
                <p>Changing your password signs you out of every other device and browser.</p>
                <label className="pulse-v2-confirm-field">
                  Current password
                  <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} autoComplete="current-password" />
                </label>
                <label className="pulse-v2-confirm-field">
                  New password
                  <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} autoComplete="new-password" />
                </label>
                <label className="pulse-v2-confirm-field">
                  Confirm new password
                  <input type="password" value={confirmPassword} onChange={(event) => setConfirmPassword(event.target.value)} autoComplete="new-password" />
                </label>
                {formError ? <div className="pulse-v2-action-message error">{formError}</div> : null}
                {passwordChangeError ? <div className="pulse-v2-action-message error">{passwordChangeError}</div> : null}
              </>
            )}
            <div className="pulse-v2-confirm-actions">
              <button type="button" className="pulse-v2-outline-action" onClick={() => setPasswordModalOpen(false)} disabled={passwordChangeBusy}>
                Cancel
              </button>
              {!signedInWithGoogle ? (
                <button
                  type="button"
                  className="pulse-v2-primary"
                  onClick={handleSubmitPasswordChange}
                  disabled={passwordChangeBusy || !currentPassword || !newPassword || !confirmPassword}
                >
                  {passwordChangeBusy ? "Updating..." : "Update password"}
                </button>
              ) : null}
            </div>
          </div>
        </div>
      ) : null}

      {twoFactorStep !== "closed" ? (
        <div className="pulse-v2-confirm-overlay" role="presentation" onClick={() => !twoFactorBusy && closeTwoFactorModal()}>
          <div className="pulse-v2-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="pulse-2fa-title" onClick={(event) => event.stopPropagation()}>
            <span className="pulse-v2-confirm-icon neutral">
              <SecurityOutlinedIcon fontSize="small" />
            </span>
            {twoFactorStep === "setup" ? (
              <>
                <h3 id="pulse-2fa-title">Set up two-factor authentication</h3>
                <p>Scan this QR code with your authenticator app (Google Authenticator, Authy, 1Password, etc).</p>
                <div className="pulse-v2-2fa-qr">
                  {twoFactorQrDataUrl ? (
                    <img src={twoFactorQrDataUrl} alt="Scan with your authenticator app" width={220} height={220} />
                  ) : (
                    <div className="pulse-v2-2fa-qr-fallback">Loading QR code...</div>
                  )}
                </div>
                <p>Or enter this code manually:</p>
                <code className="pulse-v2-2fa-secret">{twoFactorSecret}</code>
                <label className="pulse-v2-confirm-field">
                  Enter the 6-digit code from your app
                  <input
                    type="text"
                    inputMode="numeric"
                    value={twoFactorSetupCode}
                    onChange={(event) => setTwoFactorSetupCode(event.target.value)}
                    placeholder="123456"
                    maxLength={6}
                    autoFocus
                  />
                </label>
                {twoFactorSetupError ? <div className="pulse-v2-action-message error">{twoFactorSetupError}</div> : null}
                <div className="pulse-v2-confirm-actions">
                  <button type="button" className="pulse-v2-outline-action" onClick={closeTwoFactorModal} disabled={twoFactorBusy}>
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="pulse-v2-primary"
                    onClick={handleSubmitTwoFactorSetup}
                    disabled={twoFactorBusy || twoFactorSetupCode.trim().length !== 6}
                  >
                    {twoFactorBusy ? "Verifying..." : "Verify & enable"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <h3 id="pulse-2fa-title">Save your recovery codes</h3>
                <p>
                  Two-factor authentication is now on. Save these recovery codes somewhere safe - each one can be
                  used once to sign in if you lose access to your authenticator app. <strong>They won't be shown again.</strong>
                </p>
                <ul className="pulse-v2-recovery-codes">
                  {recoveryCodes.map((code) => (
                    <li key={code}>{code}</li>
                  ))}
                </ul>
                <div className="pulse-v2-confirm-actions">
                  <button type="button" className="pulse-v2-outline-action" onClick={handleCopyRecoveryCodes}>
                    {recoveryCodesCopied ? "Copied" : "Copy codes"}
                  </button>
                  <button type="button" className="pulse-v2-outline-action" onClick={handleDownloadRecoveryCodes}>
                    Download .txt
                  </button>
                  <button type="button" className="pulse-v2-primary" onClick={closeTwoFactorModal}>
                    I've saved these
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : null}

      {disableModalOpen ? (
        <div className="pulse-v2-confirm-overlay" role="presentation" onClick={() => !twoFactorBusy && setDisableModalOpen(false)}>
          <div className="pulse-v2-confirm-modal" role="dialog" aria-modal="true" aria-labelledby="pulse-2fa-disable-title" onClick={(event) => event.stopPropagation()}>
            <span className="pulse-v2-confirm-icon neutral">
              <SecurityOutlinedIcon fontSize="small" />
            </span>
            <h3 id="pulse-2fa-disable-title">Turn off two-factor authentication</h3>
            <p>Enter a code from your authenticator app, or one of your recovery codes, to confirm.</p>
            <label className="pulse-v2-confirm-field">
              Code
              <input
                type="text"
                value={disableCode}
                onChange={(event) => setDisableCode(event.target.value)}
                placeholder="123456 or a recovery code"
                autoFocus
              />
            </label>
            {disableError ? <div className="pulse-v2-action-message error">{disableError}</div> : null}
            <div className="pulse-v2-confirm-actions">
              <button type="button" className="pulse-v2-outline-action" onClick={() => setDisableModalOpen(false)} disabled={twoFactorBusy}>
                Cancel
              </button>
              <button type="button" className="pulse-v2-confirm-danger-btn" onClick={handleSubmitDisable} disabled={twoFactorBusy || !disableCode.trim()}>
                {twoFactorBusy ? "Turning off..." : "Turn off"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

const SettingsToggleRow: React.FC<{
  icon: React.ReactNode;
  label: string;
  description: string;
  checked: boolean;
  onToggle: () => void;
}> = ({ icon, label, description, checked, onToggle }) => (
  <div className="pulse-v2-account-row">
    <span className="pulse-v2-account-row-icon">{icon}</span>
    <div>
      <strong>{label}</strong>
      <small>{description}</small>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      className={`pulse-v2-switch ${checked ? "on" : ""}`}
      onClick={onToggle}
    />
  </div>
);

const ManageIrisGoals: React.FC<{
  goalOptions: Array<{ id: string; label: string; icon: React.ReactNode }>;
  selectedGoals: string[];
  onSave: (goals: string[]) => void;
}> = ({ goalOptions, selectedGoals, onSave }) => {
  const [draft, setDraft] = useState<string[]>(selectedGoals);
  const [saved, setSaved] = useState(false);

  const toggleGoal = (goalId: string) => {
    setDraft((prev) => {
      const exists = prev.includes(goalId);
      const next = exists ? prev.filter((id) => id !== goalId) : prev.length >= 5 ? prev : [...prev, goalId];
      return next;
    });
    setSaved(false);
  };

  const dirty = JSON.stringify([...draft].sort()) !== JSON.stringify([...selectedGoals].sort());
  const valid = draft.length >= 2 && draft.length <= 5;

  return (
    <section className="pulse-v2-card pulse-v2-goals-card">
      <span className="pulse-v2-settings-subhead">Choose Goals</span>
      <p className="pulse-v2-goals-hint">Choose 2&ndash;5 goals. IRIS orients your Pulse Score and recommendations around them.</p>
      <div className="pulse-v2-goal-grid">
        {goalOptions.map((goal) => {
          const selected = draft.includes(goal.id);
          return (
            <button
              key={goal.id}
              type="button"
              className={`pulse-v2-goal-option ${selected ? "selected" : ""}`}
              onClick={() => toggleGoal(goal.id)}
            >
              <span className="pulse-v2-goal-icon">{goal.icon}</span>
              <span>{goal.label}</span>
              {selected ? <CheckRoundedIcon className="pulse-v2-goal-check" fontSize="small" /> : null}
            </button>
          );
        })}
      </div>
      <div className="pulse-v2-goal-footer">
        <span>{draft.length} goal{draft.length === 1 ? "" : "s"} selected</span>
        <button
          type="button"
          className="pulse-v2-primary"
          disabled={!dirty || !valid}
          onClick={() => {
            onSave(draft);
            setSaved(true);
          }}
        >
          {saved && !dirty ? "Saved" : "Save changes"}
        </button>
      </div>
    </section>
  );
};

const IRIS_JOURNEY_OPTIONS: Array<{ value: "building" | "established"; label: string }> = [
  { value: "building", label: "Building my audience" },
  { value: "established", label: "Established creator" },
];

const IrisVoiceEditor: React.FC<{
  value: PulsePreferences;
  onSave: (value: PulsePreferences) => Promise<void>;
}> = ({ value, onSave }) => {
  const [journey, setJourney] = useState<"building" | "established">(value.creator_segment || "building");
  const [voiceProfile, setVoiceProfile] = useState(value.voice_profile || "");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dirty = journey !== (value.creator_segment || "building") || voiceProfile !== (value.voice_profile || "");

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave({ creator_segment: journey, voice_profile: voiceProfile });
      setSaved(true);
    } catch {
      setError("Could not save changes. Please retry.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="pulse-v2-card pulse-v2-goals-card pulse-v2-voice-card">
      <span className="pulse-v2-settings-subhead">Creator profile</span>
      <p className="pulse-v2-goals-hint">How IRIS understands your stage and sounds when it talks to you.</p>

      <label className="pulse-v2-voice-field">
        Creator journey
        <div className="pulse-v2-journey-options">
          {IRIS_JOURNEY_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              className={`pulse-v2-goal-option ${journey === option.value ? "selected" : ""}`}
              onClick={() => {
                setJourney(option.value);
                setSaved(false);
              }}
            >
              <span>{option.label}</span>
              {journey === option.value ? <CheckRoundedIcon className="pulse-v2-goal-check" fontSize="small" /> : null}
            </button>
          ))}
        </div>
      </label>

      <label className="pulse-v2-voice-field">
        IRIS voice profile
        <textarea
          rows={5}
          placeholder="Describe the tone, wording, and examples IRIS should follow."
          value={voiceProfile}
          onChange={(event) => {
            setVoiceProfile(event.target.value);
            setSaved(false);
          }}
        />
        <small>Your saved voice profile is applied to future IRIS replies.</small>
      </label>

      {error ? <div className="pulse-v2-action-message error">{error}</div> : null}
      <div className="pulse-v2-goal-footer pulse-v2-voice-footer">
        <button type="button" className="pulse-v2-primary" disabled={!dirty || saving} onClick={handleSave}>
          {saving ? "Saving..." : saved && !dirty ? "Saved" : "Save changes"}
        </button>
      </div>
    </section>
  );
};

export const PulseSettingsDashboard: React.FC<{
  preferences: PulsePreferences;
  onPreferencesSave: (value: PulsePreferences) => Promise<void>;
  profile: DesignerProfile;
  profileSaving: boolean;
  profileError: string | null;
  onProfileSave: (patch: { display_name?: string; bio?: string }) => Promise<void>;
  onCheckHandleAvailable: (value: string) => Promise<boolean>;
  onSaveHandle: (value: string) => Promise<void>;
  avatarUploading: boolean;
  avatarError: string | null;
  onUploadAvatar: (file: File) => Promise<void>;
  instagramAvailable: boolean;
  accountEmail: string | null;
  accountAuthProvider: string | null;
  onLogout: () => void;
  passwordChangeBusy: boolean;
  passwordChangeMessage: string | null;
  passwordChangeError: string | null;
  onChangePassword: (currentPassword: string, newPassword: string) => Promise<boolean>;
  deleteAccountBusy: boolean;
  deleteAccountError: string | null;
  onRequestAccountDeletion: (password?: string) => Promise<boolean>;
  twoFactorEnabled: boolean;
  twoFactorBusy: boolean;
  onEnable2fa: () => Promise<{ secret: string; otpauth_url: string } | null>;
  onVerify2fa: (code: string) => Promise<{ ok: true; recovery_codes?: string[] } | null>;
  onDisable2fa: (code: string) => Promise<boolean>;
  account: Account;
  draft: Account;
  preview: DesignerPreview | null;
  saving: boolean;
  error: string | null;
  youtubeConnected: boolean;
  youtubeBusy: boolean;
  youtubeError: string | null;
  instagramConnection: DesignerInstagramConnection | null;
  instagramBusy: boolean;
  instagramError: string | null;
  onDraftChange: (draft: Account) => void;
  onSave: () => void;
  onConnectYoutube: () => void;
  onDisconnectYoutube: () => void;
  onConnectInstagram: () => void;
  onDisconnectInstagram: () => void;
  messagePrefs: { weekly_digest: boolean; push_recommendations: boolean; sponsor_alerts: boolean; product_updates: boolean };
  onToggleMessagePref: (key: "weekly_digest" | "push_recommendations" | "sponsor_alerts" | "product_updates") => void;
  privacyPrefs: { personalize_insights: boolean; share_benchmarks: boolean; iris_reference_saved: boolean };
  onTogglePrivacyPref: (key: "personalize_insights" | "share_benchmarks" | "iris_reference_saved") => void;
  textScale: number;
  onTextSizeStep: (direction: 1 | -1) => void;
  irisMemoryEnabled: boolean;
  onToggleIrisMemory: () => void;
  onClearIrisMemory: () => void;
  goalOptions: Array<{ id: string; label: string; icon: React.ReactNode }>;
  selectedGoals: string[];
  onSaveGoals: (goals: string[]) => void;
}> = ({
  account,
  preview,
  youtubeConnected,
  youtubeBusy,
  youtubeError,
  instagramConnection,
  instagramBusy,
  instagramError,
  onConnectYoutube,
  onDisconnectYoutube,
  onConnectInstagram,
  onDisconnectInstagram,
  messagePrefs,
  onToggleMessagePref,
  privacyPrefs,
  onTogglePrivacyPref,
  textScale,
  onTextSizeStep,
  irisMemoryEnabled,
  onToggleIrisMemory,
  onClearIrisMemory,
  goalOptions,
  selectedGoals,
  onSaveGoals,
  preferences,
  onPreferencesSave,
  profile,
  profileSaving,
  profileError,
  onProfileSave,
  onCheckHandleAvailable,
  onSaveHandle,
  avatarUploading,
  avatarError,
  onUploadAvatar,
  instagramAvailable,
  accountEmail,
  accountAuthProvider,
  onLogout,
  passwordChangeBusy,
  passwordChangeMessage,
  passwordChangeError,
  onChangePassword,
  deleteAccountBusy,
  deleteAccountError,
  onRequestAccountDeletion,
  twoFactorEnabled,
  twoFactorBusy,
  onEnable2fa,
  onVerify2fa,
  onDisable2fa,
}) => {
  const [section, setSection] = useState<
    "profile" | "account" | "connections" | "messages" | "appearance" | "privacy" | "manage-iris"
  >("profile");
  const name = creatorName(account, preview);
  const handle = creatorHandle(account, preview);
  const instagramConnected = Boolean(instagramConnection?.connected);
  return (
    <div className="pulse-v2-page pulse-v2-settings">
      <header className="pulse-v2-page-head"><div><h1>Settings</h1><p>Manage your profile, account, connected platforms, and how IRIS works for you.</p></div></header>
      <div className="pulse-v2-settings-layout">
        <nav>
          <button className={section === "profile" ? "active" : ""} type="button" onClick={() => setSection("profile")}><PersonOutlineRoundedIcon fontSize="small" /> Profile</button>
          <button className={section === "account" ? "active" : ""} type="button" onClick={() => setSection("account")}><SecurityOutlinedIcon fontSize="small" /> Account & security</button>
          <button className={section === "connections" ? "active" : ""} type="button" onClick={() => setSection("connections")}><LinkRoundedIcon fontSize="small" /> Connected accounts</button>
          <button className={section === "messages" ? "active" : ""} type="button" onClick={() => setSection("messages")}><MailOutlineRoundedIcon fontSize="small" /> Message preferences</button>
          <button className={section === "appearance" ? "active" : ""} type="button" onClick={() => setSection("appearance")}><PaletteOutlinedIcon fontSize="small" /> Appearance</button>
          <button className={section === "privacy" ? "active" : ""} type="button" onClick={() => setSection("privacy")}><LockOutlinedIcon fontSize="small" /> Privacy controls</button>
          <button className={section === "manage-iris" ? "active" : ""} type="button" onClick={() => setSection("manage-iris")}><VisibilityOutlinedIcon fontSize="small" /> Manage IRIS</button>
        </nav>
        {section === "profile" ? (
          <main>
            <h2>Profile</h2>
            <p>How you appear across Pulse and your Media Kit</p>
            <ProfileEditor
              key={`${profile.display_name || ""}|${profile.handle || ""}|${profile.bio || ""}|${profile.avatar_url || ""}`}
              profile={profile}
              fallbackName={name}
              saving={profileSaving}
              error={profileError}
              onSave={onProfileSave}
              onCheckHandleAvailable={onCheckHandleAvailable}
              onSaveHandle={onSaveHandle}
              avatarUploading={avatarUploading}
              avatarError={avatarError}
              onUploadAvatar={onUploadAvatar}
            />
          </main>
        ) : section === "account" ? (
          <main>
            <h2>Account & security</h2>
            <p>Manage your login, password, and account access</p>
            <AccountSecurityEditor
              email={accountEmail}
              authProvider={accountAuthProvider}
              onLogout={onLogout}
              passwordChangeBusy={passwordChangeBusy}
              passwordChangeMessage={passwordChangeMessage}
              passwordChangeError={passwordChangeError}
              onChangePassword={onChangePassword}
              deleteAccountBusy={deleteAccountBusy}
              deleteAccountError={deleteAccountError}
              onRequestAccountDeletion={onRequestAccountDeletion}
              twoFactorEnabled={twoFactorEnabled}
              twoFactorBusy={twoFactorBusy}
              onEnable2fa={onEnable2fa}
              onVerify2fa={onVerify2fa}
              onDisable2fa={onDisable2fa}
            />
          </main>
        ) : section === "messages" ? (
          <main>
            <h2>Message preferences</h2>
            <p>Choose how Pulse and IRIS reach you</p>
            <section className="pulse-v2-card pulse-v2-toggle-list">
              <SettingsToggleRow
                icon={<NotificationsNoneRoundedIcon fontSize="small" />}
                label="Weekly IRIS digest"
                description="A Monday-morning email summarizing your Pulse Score and top actions"
                checked={messagePrefs.weekly_digest}
                onToggle={() => onToggleMessagePref("weekly_digest")}
              />
              <SettingsToggleRow
                icon={<NotificationsNoneRoundedIcon fontSize="small" />}
                label="Push notifications for new recommendations"
                description="Get notified the moment IRIS surfaces a new action"
                checked={messagePrefs.push_recommendations}
                onToggle={() => onToggleMessagePref("push_recommendations")}
              />
              <SettingsToggleRow
                icon={<NotificationsNoneRoundedIcon fontSize="small" />}
                label="Sponsor opportunity alerts"
                description="Hear about inbound partnership interest as soon as it comes in"
                checked={messagePrefs.sponsor_alerts}
                onToggle={() => onToggleMessagePref("sponsor_alerts")}
              />
              <SettingsToggleRow
                icon={<NotificationsNoneRoundedIcon fontSize="small" />}
                label="Product updates & announcements"
                description="Occasional news about new Pulse features"
                checked={messagePrefs.product_updates}
                onToggle={() => onToggleMessagePref("product_updates")}
              />
            </section>
          </main>
        ) : section === "appearance" ? (
          <main>
            <h2>Appearance</h2>
            <p>Adjust how Pulse looks on your screen</p>
            <section className="pulse-v2-card pulse-v2-textsize-row">
              <span className="pulse-v2-account-row-icon">
                <TextFieldsRoundedIcon fontSize="small" />
              </span>
              <div>
                <strong>Text size</strong>
                <small>Default &middot; affects the whole app</small>
              </div>
              <div className="pulse-v2-textsize-control">
                <button
                  type="button"
                  onClick={() => onTextSizeStep(-1)}
                  aria-label="Decrease text size"
                  disabled={textScale <= TEXT_SCALE_MIN}
                >
                  <RemoveRoundedIcon fontSize="small" />
                </button>
                <span>{Math.round(textScale * 100)}%</span>
                <button
                  type="button"
                  onClick={() => onTextSizeStep(1)}
                  aria-label="Increase text size"
                  disabled={textScale >= TEXT_SCALE_MAX}
                >
                  <AddRoundedIcon fontSize="small" />
                </button>
              </div>
            </section>
          </main>
        ) : section === "privacy" ? (
          <main>
            <h2>Privacy controls</h2>
            <p>Control how your data powers Pulse and IRIS</p>
            <section className="pulse-v2-card pulse-v2-privacy-notice">
              <span className="pulse-v2-account-row-icon">
                <LockOutlinedIcon fontSize="small" />
              </span>
              <div>
                <strong>How your data is used</strong>
                <small>Your data powers Pulse's engine and is never sold to third parties</small>
              </div>
            </section>
            <section className="pulse-v2-card pulse-v2-toggle-list">
              <SettingsToggleRow
                icon={<ShieldOutlinedIcon fontSize="small" />}
                label="Personalize insights using my content data"
                description="Lets IRIS tailor recommendations to your channel history"
                checked={privacyPrefs.personalize_insights}
                onToggle={() => onTogglePrivacyPref("personalize_insights")}
              />
              <SettingsToggleRow
                icon={<ShieldOutlinedIcon fontSize="small" />}
                label="Share anonymized performance benchmarks"
                description="Helps IRIS compare your channel against similar creators, never identifiable"
                checked={privacyPrefs.share_benchmarks}
                onToggle={() => onTogglePrivacyPref("share_benchmarks")}
              />
              <SettingsToggleRow
                icon={<ShieldOutlinedIcon fontSize="small" />}
                label="Let IRIS reference saved insights in chat"
                description="IRIS can bring up items from your Memory when relevant"
                checked={privacyPrefs.iris_reference_saved}
                onToggle={() => onTogglePrivacyPref("iris_reference_saved")}
              />
            </section>
          </main>
        ) : section === "manage-iris" ? (
          <main>
            <h2>Manage IRIS</h2>
            <p>Control what IRIS remembers, the goals it optimizes for, and how it talks to you</p>
            <section className="pulse-v2-card pulse-v2-toggle-list">
              <span className="pulse-v2-settings-subhead">IRIS & Memory</span>
              <SettingsToggleRow
                icon={<VisibilityOutlinedIcon fontSize="small" />}
                label="IRIS memory"
                description="Let IRIS remember insights across sessions"
                checked={irisMemoryEnabled}
                onToggle={onToggleIrisMemory}
              />
              <div className="pulse-v2-account-row">
                <span className="pulse-v2-account-row-icon">
                  <DeleteOutlineRoundedIcon fontSize="small" />
                </span>
                <div>
                  <strong>Clear memory</strong>
                  <small>Wipe everything IRIS has learned</small>
                </div>
                <button type="button" className="pulse-v2-link-action" onClick={onClearIrisMemory}>
                  Clear
                </button>
              </div>
            </section>
            <ManageIrisGoals goalOptions={goalOptions} selectedGoals={selectedGoals} onSave={onSaveGoals} />
            <IrisVoiceEditor key={`${preferences.creator_segment || ""}|${preferences.voice_profile || ""}`} value={preferences} onSave={onPreferencesSave} />
          </main>
        ) : (
          <main className="pulse-v2-connections"><h2>Connected accounts</h2><p>Read-only analytics sources available to IRIS</p><section className="pulse-v2-card pulse-v2-connection-list">
            <article><span className="youtube"><YouTubeIcon /></span><div><strong>YouTube</strong><small>{youtubeConnected ? `${handle} · Analytics connected` : "Connect channel analytics"}</small></div><button type="button" onClick={youtubeConnected ? onDisconnectYoutube : onConnectYoutube} disabled={youtubeBusy}>{youtubeBusy ? "Working..." : youtubeConnected ? "Disconnect" : "Connect"}</button></article>
            <article><span className="instagram"><InstagramIcon /></span><div><strong>Instagram</strong><small>{instagramConnected ? `@${instagramConnection?.username || "connected"} · ${compactNumber(instagramConnection?.followers_count)} followers` : instagramAvailable ? "Professional Creator or Business account" : "Coming soon - connection is not enabled yet"}</small></div><button type="button" onClick={instagramConnected ? onDisconnectInstagram : onConnectInstagram} disabled={instagramBusy || (!instagramConnected && !instagramAvailable)}>{instagramBusy ? "Working..." : instagramConnected ? "Disconnect" : instagramAvailable ? "Connect" : "Coming soon"}</button></article>
            {youtubeError ? <div className="pulse-v2-action-message error">{youtubeError}</div> : null}
            {instagramError ? <div className="pulse-v2-action-message error">{instagramError}</div> : null}
            <p>Pulse keeps each platform's audience and performance totals separate. Instagram access is read-only.</p>
          </section></main>
        )}
      </div>
    </div>
  );
};
